<template>
  <div class="black-order-page" :class="blackThemeClass">
    <header class="brand-hero">
      <div class="brand-copy">
        <h1>{{ storeName }}</h1>
        <p>{{ storeSlogan }}</p>
        <button type="button" @click="openOrders">查看我的订单 <span>〉</span></button>
      </div>
      <template v-if="recommendGoodsList.length">
        <van-swipe class="recommend-banner" :autoplay="recommendGoodsList.length > 1 ? 5000 : 0"
          :loop="recommendGoodsList.length > 1" :show-indicators="false" @change="handleRecommendChange">
          <van-swipe-item v-for="goods in recommendGoodsList" :key="goods.Id || goods.GoodsCode">
            <div class="recommend-slide" @click="openDetail(goods)">
              <img :src="goods.PicUrl || placeholderImage" :alt="goods.GoodsName" />
              <div class="recommend-copy" >
                <strong>今日推荐</strong>
                <span>{{ goods.Description || "Today's Recommendation" }}</span>
              </div>
            </div>
          </van-swipe-item>
        </van-swipe>
        <div v-if="recommendGoodsList.length > 1" class="banner-dots" aria-hidden="true">
          <i v-for="(goods, index) in recommendGoodsList" :key="`recommend-dot-${goods.Id || goods.GoodsCode || index}`"
            :class="{ active: recommendActiveIndex === index }"></i>
        </div>
      </template>
    </header>

    <div class="search-box" :class="{ 'is-search-active': searchInputActive }" @click="focusSearchInput">
      <!-- <span class="search-icon" aria-hidden="true"></span> -->
      <input
        ref="searchInput"
        v-model.trim="searchKeyword"
        type="text"
        inputmode="search"
        autocomplete="off"
        placeholder="搜索你感兴趣的美食吧～"
        @focus="handleSearchFocus"
        @blur="handleSearchBlur"
      />
      <button
        v-show="searchInputActive"
        type="button"
        class="search-clear-btn"
        aria-label="清空搜索"
        @mousedown.prevent.stop="clearSearchKeyword"
        @touchend.prevent.stop="clearSearchKeyword"
      >
        ×
      </button>
    </div>

    <main class="menu-layout">
      <aside class="category-list">
        <button v-for="category in categoryList" :key="category.id" type="button"
          :class="{ active: activeCategoryId === category.id }" @click="selectCategory(category.id)">
          <img class="category-icon" :src="category.icon" alt="" aria-hidden="true" />
          <span class="category-name">{{ category.name }}</span>
          <i v-if="category.count">{{ category.count }}</i>
        </button>
      </aside>

      <section class="goods-panel">
        <div class="category-heading">
          <strong>{{ activeCategoryName }}</strong>
          <span>Selected dishes</span>
        </div>

        <div v-if="loading" class="state-message">菜品加载中...</div>
        <div v-else-if="!visibleGoods.length" class="state-message">暂无相关菜品</div>
        <template v-else>
          <article v-for="goods in visibleGoods" :key="goods.Id" class="goods-card">
            <div v-if="isFullySoldOut(goods)" class="sold-out-mask">售罄</div>
            <div class="goods-image"  @click="openDetail(goods)">
              <img :src="goods.PicUrl || placeholderImage" :alt="goods.GoodsName" />
              <span v-if="goods.IsRecommend" class="recommend-tag">招牌菜</span>
              <!-- <button type="button" class="detail-button" @click="openDetail(goods)">详情 〉</button> -->
            </div>
            <div class="goods-info">
              <div>
                <strong>{{ goods.GoodsName }}</strong>
                <!-- <span>{{ }}</span> -->
              </div>
              <div v-if="!isFullySoldOut(goods)" class="goods-action">
                <p>¥{{ formatMoney(getGoodsPrice(goods)) }} 元<small>/{{ goods.Unit || "份" }}</small></p>
                <button v-if="isSetMeal(goods)" type="button" @click="openSetMeal(goods)">
                  选套餐
                </button>
                <button v-else-if="needsSpecification(goods)" type="button" @click="openSpecification(goods)">
                  选规格
                </button>
                <div v-else-if="getSimpleGoodsQuantity(goods)" class="card-quantity quantity-control">
                  <button type="button" @click="changeSimpleGoodsQuantity(goods, -1)">−</button>
                  <span>{{ getSimpleGoodsQuantity(goods) }}</span>
                  <button type="button" @click="changeSimpleGoodsQuantity(goods, 1)">＋</button>
                </div>
                <button v-else type="button" class="add-button" @click="addSimpleGoods(goods)">＋</button>
              </div>
            </div>
          </article>
        </template>
      </section>
    </main>

    <footer class="cart-bar" @click="openCart">
      <div class="cart-symbol">
        <span class="cart-bowl" aria-hidden="true">♨</span>
        <i v-if="cartTotalCount">{{ cartTotalCount }}</i>
      </div>
      <div class="cart-summary">
        <span v-if="!cartTotalCount">空空如也，快来选购吧</span>
        <template v-else>
          <strong>¥{{ formatMoney(cartTotalMoney) }}</strong>
          <span>共 {{ cartTotalCount }} 份</span>
        </template>
      </div>
      <button type="button" :disabled="isSubmitting" @click.stop="handleSubmit">去下单</button>
    </footer>

    <van-popup
      v-model="showDetailPopup"
      class="black-popup detail-popup-shell"
      :class="blackThemeClass"
      position="bottom"
      round
      @closed="resetDetailPopup"
    >
      <section v-if="detailGoods" class="detail-popup">
        <header class="popup-header">
          <div>
            <strong>{{ detailGoods.GoodsName || "今日推荐" }}</strong>
            <span>{{ detailGoods.Description || "甄选当季食材，匠心烹制" }}</span>
          </div>
          <button type="button" @click="closeDetail">×</button>
        </header>
        <img :src="detailGoods.PicUrl || placeholderImage" :alt="detailGoods.GoodsName || '今日推荐'" />
        <div class="detail-copy">
          <p>{{ detailGoods.Detail || detailGoods.Description || "无更多介绍" }}</p>
          <div class="detail-price-row" :class="{ 'setmeal-detail-price-row': isSetMeal(detailGoods) }">
            <strong>
              ¥{{ formatMoney(getGoodsPrice(detailGoods)) }}
              <small>/{{ detailGoods.Unit || "份" }}</small>
            </strong>
            <button v-if="isSetMeal(detailGoods)" type="button" class="detail-setmeal-button" @click="addDetailGoods">
              选套餐
            </button>
            <div v-else class="card-quantity quantity-control">
              <button type="button" :disabled="detailQuantity <= detailMinimumQuantity"
                @click="changeDetailQuantity(-1)">
                −
              </button>
              <span>{{ detailQuantity }}</span>
              <button type="button" :disabled="detailQuantity >= 99" @click="changeDetailQuantity(1)">
                ＋
              </button>
            </div>
          </div>
        </div>
        <footer v-if="!isSetMeal(detailGoods)" class="single-popup-footer">
          <button type="button" @click="addDetailGoods">加入购物车</button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showCartPopup"
      class="black-popup cart-popup-shell"
      :class="blackThemeClass"
      position="bottom"
      round
      @closed="resetCartPopup"
    >
      <section class="cart-popup">
        <header class="popup-header">
          <strong>已选菜品</strong>
          <button type="button" :disabled="!cartItems.length" @click="requestClearCart">
            一键清空
          </button>
        </header>
        <div class="cart-list">
          <div v-if="!cartItems.length" class="state-message">购物车还是空的</div>
          <div v-for="item in cartItems" :key="item.key" class="cart-item">
            <img :src="item.image || placeholderImage" alt="" />
            <div class="cart-item-info">
              <strong>{{ item.name }}</strong>
              <!-- <span>{{ item.specName }}{{ item.remarkText ? ` · ${item.remarkText}` : "" }}</span> -->
              <!-- <button
                v-if="item.isSetMeal"
                type="button"
                class="edit-setmeal-button"
                @click="openSetMeal(item.sourceGoods, item)"
              >
                修改套餐
              </button> -->
              <p>¥{{ formatMoney(item.price) }}</p>
            </div>
            <div class="card-quantity quantity-control">
              <button type="button" @click="changeCartQuantity(item.key, -1)">−</button>
              <span>{{ item.quantity }}</span>
              <button type="button" @click="changeCartQuantity(item.key, 1)">＋</button>
            </div>
          </div>
        </div>
        <div class="cart-total">
          <span>共 {{ cartTotalCount }} 份</span>
          <strong>合计 ¥{{ formatMoney(cartTotalMoney) }}</strong>
        </div>
        <footer class="popup-footer">
          <button type="button" class="close-button" @click="closeCart">关闭</button>
          <button type="button" class="submit-button" :disabled="!cartItems.length || isSubmitting"
            @click="handleSubmit">
            去提交
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showClearCartConfirm"
      class="clear-cart-confirm-shell"
      :class="blackThemeClass"
      position="center"
      :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.76)' }"
      :close-on-click-overlay="false"
      round
    >
      <section class="clear-cart-confirm">
        <strong>清空购物车</strong>
        <p>确定要清空全部已选菜品吗？</p>
        <div>
          <button type="button" class="cancel-button" @click="cancelClearCart">
            取消
          </button>
          <button type="button" class="confirm-button" @click="confirmClearCart">
            确定清空
          </button>
        </div>
      </section>
    </van-popup>

    <van-popup
      v-model="showSpecificationPopup"
      class="black-popup spec-popup-shell"
      :class="blackThemeClass"
      position="bottom"
      round
      @closed="resetSpecificationPopup"
    >
      <section v-if="editingGoods" class="spec-popup">
        <header class="popup-header">
          <div>
            <strong>{{ editingGoods.GoodsName }}</strong>
            <span v-if="!isBlackThemeV2">请选择规格和备注</span>
          </div>
          <button type="button" @click="closeSpecification">×</button>
        </header>

        <div class="spec-content">
          <div class="option-group">
            <h3>{{ isBlackThemeV2 ? "份量" : "大小份" }}</h3>
            <div class="option-grid">
              <button v-for="spec in specificationOptions" :key="spec.id" type="button"
                :class="{ active: selectedSpecificationId === spec.id }" @click="selectedSpecificationId = spec.id">
                {{ spec.name }} <span v-if="!isBlackThemeV2">¥{{ formatMoney(spec.price) }}</span>
              </button>
            </div>
          </div>
          <div v-if="remarkOptions.length" class="option-group">
            <h3>{{ isBlackThemeV2 ? "忌口" : "备注信息" }}</h3>
            <div class="option-grid">
              <button v-for="remark in remarkOptions" :key="remark" type="button"
                :class="{ active: selectedRemarks.includes(remark) }" @click="toggleRemark(remark)">
                {{ remark }}
              </button>
            </div>
          </div>
          <div class="option-group">
            <h3>{{ isBlackThemeV2 ? "备注 (非必选) ：" : "手动备注" }}</h3>
            <textarea
              v-model.trim="customRemark"
              maxlength="80"
              :placeholder="isBlackThemeV2 ? '如有过敏源请备注' : '请输入口味、忌口等备注信息'"
            ></textarea>
          </div>
        </div>
        <footer class="spec-footer">
          <div>
            <span v-if="!isBlackThemeV2">单价</span>
            <strong>
              ¥{{ formatMoney(selectedSpecificationPrice)
              }}<template v-if="isBlackThemeV2">/份</template>
            </strong>
          </div>
          <button type="button" :disabled="!selectedSpecificationId" @click="confirmSpecification">
            加入购物车
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showSetMealPopup"
      class="black-popup setmeal-popup-shell"
      :class="blackThemeClass"
      position="bottom"
      round
      @closed="resetSetMealPopup"
    >
      <section class="setmeal-popup">
        <header class="popup-header">
          <div>
            <strong>{{ setMealInfo.GoodsName || (editingSetMealGoods && editingSetMealGoods.GoodsName) }}</strong>
            <!-- <span>请按每组要求选择套餐内容</span> -->
          </div>
          <button type="button" @click="closeSetMeal">×</button>
        </header>

        <div v-if="setMealLoading" class="state-message">套餐加载中...</div>
        <div v-else-if="!setMealGroups.length" class="state-message">暂无可选套餐内容</div>
        <div v-else class="setmeal-content">
          <section v-for="group in setMealGroups" :key="group.id" class="setmeal-group">
            <header>
              <!-- <strong>{{ group.name }}</strong> -->
              <!-- <span>
                请选择 {{ group.selectRows }} 项，已选 {{ getSetMealGroupSelectedCount(group) }} 项
              </span> -->
            </header>
            <div v-for="item in group.items" :key="item.slotKey" class="setmeal-item"
              :class="{ active: item.selected }">
              <img :src="item.PicUrl || placeholderImage" :alt="item.GoodsName" />
              <span>
                <strong>{{ item.GoodsName }}</strong>
                <small>{{ item.Description || item.Unit || "套餐菜品" }}</small>
              </span>
              <button v-if="item.selected && hasSetMealChangeItems(item)" type="button" class="setmeal-change-button"
                @click="openSetMealChange(item)">
                更换菜品
              </button>
            </div>
          </section>
        </div>

        <footer class="spec-footer setmeal-footer">
          <div>
            <span>套餐价</span>
            <strong>¥{{ formatMoney(getGoodsPrice(editingSetMealGoods)) }}</strong>
          </div>
          <div class="setmeal-quantity-area">
            <span>套餐数量</span>
            <div class="card-quantity quantity-control">
              <button type="button" :disabled="setMealQuantity <= setMealMinimumQuantity"
                @click="changeSetMealQuantity(-1)">
                −
              </button>
              <span>{{ setMealQuantity }}</span>
              <button type="button" :disabled="setMealQuantity >= 99" @click="changeSetMealQuantity(1)">
                ＋
              </button>
            </div>
          </div>
          <button type="button" :disabled="setMealLoading || !setMealGroups.length" @click="confirmSetMeal">
            {{ editingSetMealCartKey ? "保存修改" : "加入购物车" }}
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showSetMealChangePopup"
      class="black-popup setmeal-change-popup-shell"
      :class="blackThemeClass"
      position="bottom"
      round
      @closed="resetSetMealChangePopup"
    >
      <section class="setmeal-change-popup">
        <header class="popup-header">
          <div>
            <strong>更换菜品</strong>
            <span>{{ changingSetMealItem ? changingSetMealItem.GoodsName : "" }}</span>
          </div>
          <button type="button" @click="closeSetMealChange">×</button>
        </header>
        <div class="setmeal-change-content">
          <button v-for="item in setMealChangeOptions" :key="item.id" type="button" class="setmeal-change-option"
            :class="{ active: selectedSetMealChangeId === item.id }" @click="selectSetMealChange(item)">
            <img :src="item.PicUrl || placeholderImage" :alt="item.GoodsName" />
            <span>
              <strong>{{ item.GoodsName }}</strong>
              <small>{{ item.Description || "可更换套餐菜品" }}</small>
            </span>
            <em v-if="item.Price">¥{{ formatMoney(item.Price) }}</em>
          </button>
        </div>
        <footer class="popup-footer">
          <button type="button" class="close-button" @click="closeSetMealChange">取消</button>
          <button type="button" class="submit-button" :disabled="!selectedSetMealChangeId"
            @click="confirmSetMealChange">
            确定更换
          </button>
        </footer>
      </section>
    </van-popup>
  </div>
</template>

<script>
import { getFoodList, getSetmealFoodInfo } from "@/api/foodOrder";
import {
  applyBlackGoldenUIVersion,
  handleSessionStorage,
  resolveBlackGoldenUIVersion,
} from "@/utils";
import { initConnect, handleSendMessage } from "@/utils/websorket";
import { EventBus } from "@/utils/eventbus";

const DEFAULT_CATEGORY_ICON = require("@/assets/black-micropoint/category-icons/default.svg");
const CATEGORY_ICON_RULES = [
  {
    keywords: ["小龙虾", "龙虾", "口味虾", "虾"],
    icon: require("@/assets/black-micropoint/category-icons/lobster.svg"),
  },
  {
    keywords: ["酒水", "啤酒", "红酒", "白酒", "洋酒", "饮品", "饮料", "茶饮", "咖啡", "酒",],
    icon: require("@/assets/black-micropoint/category-icons/drink.svg"),
  },
  {
    keywords: ["海鲜", "水产", "鱼", "蟹", "贝", "螺"],
    icon: require("@/assets/black-micropoint/category-icons/seafood.svg"),
  },
  {
    keywords: ["套餐", "组合", "超值餐", "双人餐", "多人餐", "一人餐"],
    icon: require("@/assets/black-micropoint/category-icons/setmeal.svg"),
  },
  {
    keywords: ["凉菜", "冷菜", "冷碟", "凉碟", "拼盘"],
    icon: require("@/assets/black-micropoint/category-icons/cold-dish.svg"),
  },
  {
    keywords: ["汤", "粥", "羹"],
    icon: require("@/assets/black-micropoint/category-icons/soup.svg"),
  },
  {
    keywords: ["烧烤", "烤串", "串串", "烤肉", "烤", "烧"],
    icon: require("@/assets/black-micropoint/category-icons/barbecue.svg"),
  },
  {
    keywords: ["主食", "米饭", "面食", "面条", "粉", "饭", "米", "米粉"],
    icon: require("@/assets/black-micropoint/category-icons/staple-food.svg"),
  },
  {
    keywords: [
      "水果", "果盘", "鲜果", "苹果", "李子", "梨", "桃", "西瓜", "无花果",
      "香蕉", "葡萄", "橙", "橘", "柑", "柚", "柠檬", "草莓", "蓝莓",
      "树莓", "樱桃", "车厘子", "芒果", "菠萝", "凤梨", "猕猴桃", "奇异果",
      "火龙果", "哈密瓜", "香瓜", "木瓜", "榴莲", "山竹", "荔枝", "龙眼",
      "桂圆", "枇杷", "石榴", "柿子", "杨梅", "椰子", "百香果", "牛油果",
      "冬枣", "红枣", "杏", "桑葚", "释迦",
    ],
    icon: require("@/assets/black-micropoint/category-icons/fruit.svg"),
  },
  {
    keywords: ["甜品", "甜点", "蛋糕", "小吃"],
    icon: require("@/assets/black-micropoint/category-icons/dessert.svg"),
  },
  {
    keywords: ["热菜", "炒菜", "菜品", "招牌菜", "小炒", "炒", "干锅", "锅"],
    icon: require("@/assets/black-micropoint/category-icons/hot-dish.svg"),
  },
];

export default {
  name: "BlackMicropoint",
  data() {
    return {
      loading: false,
      requestSequence: 0,
      isSubmitting: false,
      submitUnlockTimer: null,
      storeName: "V汤泉生活馆（北京）",
      storeSlogan: "食有风骨，味见匠心",
      heroImage: require("@/assets/black-micropoint/recommend.jpg"),
      placeholderImage: require("@/assets/black-micropoint/dish-placeholder.svg"),
      searchKeyword: "",
      searchFocused: false,
      searchBlurTimer: null,
      activeCategoryId: null,
      categoryGroups: [],
      cartItems: [],
      showDetailPopup: false,
      showCartPopup: false,
      showClearCartConfirm: false,
      showSpecificationPopup: false,
      showSetMealPopup: false,
      showSetMealChangePopup: false,
      detailGoods: null,
      detailQuantity: 1,
      detailMinimumQuantity: 1,
      editingGoods: null,
      specificationAddQuantity: 1,
      editingSetMealGoods: null,
      editingSetMealCartKey: null,
      setMealInfo: {},
      setMealGroups: [],
      setMealLoading: false,
      setMealRequestSequence: 0,
      setMealQuantity: 1,
      setMealMinimumQuantity: 1,
      changingSetMealItem: null,
      setMealChangeOptions: [],
      selectedSetMealChangeId: null,
      recommendActiveIndex: 0,
      selectedSpecificationId: null,
      selectedRemarks: [],
      customRemark: "",
      currentVpnId: "",
      previousPageChromeTheme: null,
      teamId: null,
      isGroupConfig: false,
      isSureVpn: false,
      teamGoodsListHandler: null,
      teamGoodsChangeHandler: null,
      saasCode: "",
      diningRoomId: "",
      /** 黑金 UI 子版本：1=现有，2=Figma 新主题 */
      blackUiVersion: 1,
    };
  },
  computed: {
    /** 挂到页面和弹层上的主题 class */
    blackThemeClass() {
      return this.blackUiVersion === 2 ? "black-order-theme-v2" : "";
    },
    isBlackThemeV2() {
      return this.blackUiVersion === 2;
    },
    searchInputActive() {
      return this.searchFocused || !!this.searchKeyword;
    },
    /**
     * 将接口分类转换为左侧导航需要的数据，并实时计算每个分类在购物车中的数量。
     * 分类数量来自本页面独立购物车，同一分类下不同规格的数量会合并显示。
     *
     * 图标根据分类名称关键词匹配本地黑金 SVG，不依赖接口提供图标。
     *
     * @returns {Array<{id: string|number, name: string, icon: string, count: number}>}
     * 左侧导航可直接渲染的分类数组。
     */
    categoryList() {
      return this.categoryGroups.map((group) => ({
        id: group.GoodsCatalogId,
        name: group.GoodsCatalogName,
        icon: this.getCategoryIcon(group.GoodsCatalogName),
        count: this.cartItems
          .filter((item) => item.catalogId === group.GoodsCatalogId)
          .reduce((total, item) => total + item.quantity, 0),
      }));
    },
    /**
     * 优先提取 IsRecommend 为 1 或 true 的推荐菜作为轮播数据。
     * 完全没有推荐菜时，回退到全部商品中有图片的菜品。
     *
     * @returns {Array<Object>} 最多三条真实菜品数据。
     */
    recommendGoodsList() {
      const allGoods = this.categoryGroups.reduce(
        (result, group) => result.concat(group.Goods || []),
        []
      ).filter((goods) => goods);
      const recommendGoods = allGoods.filter(
        (goods) =>
          goods.IsRecommend === 1 ||
          goods.IsRecommend === true ||
          goods.IsRecommend === "1"
      );

      if (recommendGoods.length) {
        return recommendGoods.slice(0, 3);
      }

      return allGoods.filter(
        (goods) => String(goods.PicUrl || "").trim()
      ).slice(0, 3);
    },
    /**
     * 获取当前分类名称，作为右侧菜品列表标题。
     *
     * @returns {string} 当前分类名称；没有有效分类时返回“精选菜品”。
     */
    activeCategoryName() {
      const category = this.categoryList.find(
        (item) => item.id === this.activeCategoryId
      );
      return category ? category.name : "精选菜品";
    },
    /**
     * 根据当前分类和搜索关键词过滤菜品。
     * 搜索时跨分类查询，未搜索时只显示左侧当前选中的分类。
     * 搜索字符串在比较前会去除首尾空格并转小写，避免大小写影响结果。
     *
     * @returns {Array<Object>} 符合分类或关键词条件的标准化菜品数组。
     */
    visibleGoods() {
      const keyword = String(this.searchKeyword || "").trim().toLowerCase();
      const groups = keyword
        ? this.categoryGroups
        : this.categoryGroups.filter(
          (group) => group.GoodsCatalogId === this.activeCategoryId
        );
      return groups.reduce((result, group) => {
        const goods = (group.Goods || []).filter((item) =>
          String(item.GoodsName || "")
            .toLowerCase()
            .includes(keyword)
        );
        return result.concat(goods);
      }, []);
    },
    /**
     * 汇总购物车全部条目的数量。
     *
     * @returns {number} 所有购物车条目 quantity 的合计值。
     */
    cartTotalCount() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0);
    },
    /**
     * 汇总购物车金额，保留原始数字供界面统一格式化。
     *
     * @returns {number} 每个条目“单价 × 数量”的合计金额。
     */
    cartTotalMoney() {
      return this.cartItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0
      );
    },
    /**
     * 读取当前编辑菜品的规格列表。
     * 接口没有规格时生成“标准份”，保证所有菜品都能通过新规格弹窗加入购物车。
     * 规格 id 全部转为字符串，避免数字和字符串类型不一致导致选中态失效。
     *
     * @returns {Array<{id: string, name: string, price: number}>} 可供规格弹窗渲染的规格。
     */
    specificationOptions() {
      if (!this.editingGoods) return [];
      const specifications = this.editingGoods.Specifications || [];
      if (specifications.length) {
        return specifications.map((item) => ({
          id: String(item.Id),
          name: item.Name || item.SpecificName || "标准份",
          price: this.normalizePrice(
            item.Price != null ? item.Price : this.editingGoods.Price
          ),
        }));
      }
      return [
        {
          id: "default",
          name: "标准份",
          price: this.normalizePrice(this.editingGoods.Price),
        },
      ];
    },
    /**
     * 合并接口提供的口味、做法和商品备注，去重后供备注按钮选择。
     * 空字符串和 null 会被排除，最终值统一转换为字符串。
     *
     * @returns {string[]} 去重后的备注选项。
     */
    remarkOptions() {
      if (!this.editingGoods) return [];
      return [
        ...(this.editingGoods.TasteArray || []),
        ...(this.editingGoods.MethodArray || []),
        ...(this.editingGoods.GoodsRemarkArray || []),
      ]
        .map((item) => String(item || "").trim())
        .filter((item, index, array) => item && array.indexOf(item) === index);
    },
    /**
     * 获取当前选中规格的单价。
     *
     * @returns {number} 当前选中规格价格；未选择规格时返回 0。
     */
    selectedSpecificationPrice() {
      const specification = this.specificationOptions.find(
        (item) => item.id === this.selectedSpecificationId
      );
      return specification ? specification.price : 0;
    },
  },
  watch: {
    /**
     * 深度监听黑色点餐页自己的购物车。
     * 任意新增、减少、规格修改或套餐修改都会立即写入独立会话缓存，
     * 该缓存不使用原项目 shopCart，因此不会影响 Micropoint/NewPlaceOrder。
     */
    cartItems: {
      deep: true,
      handler() {
        this.persistBlackCart();
      },
    },
  },
  created() {
    this.resolveUiVersion();
    this.applyPageChromeTheme();
    this.restoreBlackCart();
    this.initializePage();
    this.initializeTeamOrdering();
    this.queryVpnInfo()
  },
  beforeRouteLeave(to, from, next) {
    this.removeTeamOrderingListeners();
    next();
  },
  beforeDestroy() {
    if (this.submitUnlockTimer) {
      window.clearTimeout(this.submitUnlockTimer);
      this.submitUnlockTimer = null;
    }
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
      this.searchBlurTimer = null;
    }
    this.removeTeamOrderingListeners();
    this.restorePageChromeTheme();
  },
  methods: {
    /**
     * 解析黑金 UI 子版本。
     * 支持路由 query.blackUiVersion / 门店配置 BlackGoldenUIVersion / 开发模拟参数。
     */
    resolveUiVersion(source) {
      const queryVersion =
        this.$route && this.$route.query
          ? this.$route.query.blackUiVersion
          : null;
      this.blackUiVersion = resolveBlackGoldenUIVersion(
        queryVersion != null ? { blackUiVersion: queryVersion } : undefined
      );
    },

    queryVpnInfo() {
      $http(`wechath5/zh-cn/v1/vpn`).then((res) => {
        const vpnDatas = res.Items;
        const vpnItem1 = vpnDatas.find((v) => {
          return v.SaaSCode === this.saasCode;
        });
        if (vpnItem1) {
          applyBlackGoldenUIVersion(vpnItem1);
          const vpnInfo = {
            id: vpnItem1.Id,
            name: vpnItem1.Name,
            ...vpnItem1,
            ...vpnItem1.SaasVpnSetting
          }
          this.storeName = vpnItem1.Name
          this.resolveUiVersion(vpnInfo);
          handleSessionStorage.set("vpnInfo", vpnInfo);

        } else {
          const vpnItem1 = vpnDatas[0]
          applyBlackGoldenUIVersion(vpnItem1);
          const vpnInfo = {
            id: vpnItem1.Id,
            name: vpnItem1.Name,
            ...vpnItem1,
            ...vpnItem1.SaasVpnSetting
          }
          this.saasCode = vpnItem1.SaaSCode
          this.storeName = vpnItem1.Name
          this.resolveUiVersion(vpnInfo);
          handleSessionStorage.set("vpnInfo", vpnInfo);

          this.loadFoodList({
            code: this.saasCode,
            diningRoomId: this.diningRoomId,
            vpnId: vpnItem1.Id,
          });


        }
      });
    },

    /**
     * 根据分类名称选择统一黑金风格的本地 SVG 图标。
     * 规则按照数组顺序匹配，例如“小龙虾套餐”优先展示龙虾图标；
     * 没有命中任何关键词时使用通用餐盘图标，保证新增分类也能正常展示。
     *
     * @param {string} categoryName 接口返回的分类名称。
     * @returns {string} Webpack 处理后的本地图标地址。
     */
    getCategoryIcon(categoryName) {
      const normalizedName = String(categoryName || "").trim();
      const matchedRule = CATEGORY_ICON_RULES.find((rule) =>
        rule.keywords.some((keyword) => normalizedName.includes(keyword))
      );
      return matchedRule ? matchedRule.icon : DEFAULT_CATEGORY_ICON;
    },
    /**
     * 记录“今日推荐”当前轮播位置，驱动下方自定义指示点同步高亮。
     * 该回调同时响应五秒自动轮播和用户手动滑动。
     *
     * @param {number} index 当前展示的推荐菜索引。
     * @returns {void}
     */
    handleRecommendChange(index) {
      this.recommendActiveIndex = Number(index) || 0;
    },
    /**
     * 将当前 H5 页面的浏览器外观切换成深色主题。
     * theme-color 会影响支持该标准的 Android 微信顶部栏；color-scheme 和
     * apple-mobile-web-app-status-bar-style 用于支持对应能力的 WebKit 环境。
     * 同时设置 html/body 背景，可以消除页面回弹、刘海安全区露出的白色区域。
     *
     * 微信公众号的原生标题栏由微信客户端控制，部分 iOS 微信版本会忽略这些
     * Web 标准；该方法是在公众号 H5 权限范围内能够实现的最完整适配。
     */
    applyPageChromeTheme() {
      const themeColorMeta = this.getOrCreateMeta("name", "theme-color");
      const statusBarMeta = this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      const colorSchemeMeta = this.getOrCreateMeta("name", "color-scheme");

      this.previousPageChromeTheme = {
        themeColor: themeColorMeta.getAttribute("content") || "",
        statusBar: statusBarMeta.getAttribute("content") || "",
        colorScheme: colorSchemeMeta.getAttribute("content") || "",
        htmlBackground: document.documentElement.style.backgroundColor,
        bodyBackground: document.body.style.backgroundColor,
      };

      themeColorMeta.setAttribute("content", "#171717");
      statusBarMeta.setAttribute("content", "black-translucent");
      colorSchemeMeta.setAttribute("content", "dark");
      document.documentElement.style.backgroundColor = "#171717";
      document.body.style.backgroundColor = "#171717";
    },
    /**
     * 获取指定 meta；页面原本不存在时创建并追加到 head。
     * 集中封装可以避免主题初始化中重复编写 DOM 查询和创建逻辑。
     *
     * @param {string} attribute 用于定位 meta 的属性名，例如 name。
     * @param {string} value 属性值，例如 theme-color。
     * @returns {HTMLMetaElement} 已存在或新创建的 meta 元素。
     */
    getOrCreateMeta(attribute, value) {
      let meta = document.head.querySelector(`meta[${attribute}="${value}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
      return meta;
    },
    /**
     * 离开黑色点餐页时恢复进入前的浏览器主题和页面背景。
     * 这样深色配置只服务于 BlackMicropoint，不会导致项目其他浅色页面变黑。
     */
    restorePageChromeTheme() {
      if (!this.previousPageChromeTheme) {
        return;
      }
      this.getOrCreateMeta("name", "theme-color").setAttribute(
        "content",
        this.previousPageChromeTheme.themeColor
      );
      this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      ).setAttribute("content", this.previousPageChromeTheme.statusBar);
      this.getOrCreateMeta("name", "color-scheme").setAttribute(
        "content",
        this.previousPageChromeTheme.colorScheme
      );
      document.documentElement.style.backgroundColor =
        this.previousPageChromeTheme.htmlBackground;
      document.body.style.backgroundColor =
        this.previousPageChromeTheme.bodyBackground;
    },
    /**
     * 初始化门店文案并请求分类和菜品。
     * code 优先读取 saasCode，其次读取 code；另外两个参数直接透传给 getFoodList。
     * 路由对象或本地缓存不存在时使用空对象兜底，保证直接打开页面不会报错。
     *
     * @returns {void}
     */
    initializePage() {
      const query = this.$route && this.$route.query ? this.$route.query : {};
      const storedVpn = handleSessionStorage.get("vpnInfo") || {};
      this.diningRoomId = handleSessionStorage.get("diningRoomId") || "";
      this.storeName = query.storeName || storedVpn.name || this.storeName;
      this.storeSlogan = query.storeSlogan || this.storeSlogan;
      this.currentVpnId = query.vpnId || storedVpn.id || "";
      this.loadFoodList({
        code: query.saasCode || query.code || storedVpn.code || "",
        diningRoomId: query.diningRoomId || this.diningRoomId,
        vpnId: this.currentVpnId,
      });
      this.saasCode = query.saasCode
    },
    /**
     * 初始化黑色点餐页的团队点单能力。
     * 只有后台开启团队点单、当前二维码已经确认唯一门店且存在有效 TeamId 时，
     * 才建立现有项目的团队购物车连接；普通点单继续使用本页面独立缓存。
     *
     * @returns {void}
     */
    initializeTeamOrdering() {
      this.teamId = handleSessionStorage.get("teamId") || null;
      this.isGroupConfig = this.normalizeSessionBoolean(
        handleSessionStorage.get("EnableTeamOrder")
      );
      this.isSureVpn = this.normalizeSessionBoolean(
        handleSessionStorage.get("isSureVpn")
      );
      if (!this.isTeamOrderActive()) return;

      this.teamGoodsChangeHandler = (data) => {
        this.applyTeamGoodsChange(data);
      };
      this.teamGoodsListHandler = (data, isClear) => {
        if (isClear) {
          this.cartItems = [];
          return;
        }
        this.replaceCartWithTeamGoods(data);
      };
      EventBus.$on("getMessage", this.teamGoodsChangeHandler);
      EventBus.$on("getGroupGoodsList", this.teamGoodsListHandler);
      initConnect();

      if (this.$store.state.pointConnet) {
        this.requestTeamGoodsList();
      }
    },
    /**
     * 兼容会话缓存中的布尔值、数字开关和历史字符串值。
     * 明确排除 false、0 和 "false"，避免使用 Boolean("false") 时被误判为开启。
     *
     * @param {*} value sessionStorage 读取出的开关值。
     * @returns {boolean} 规范化后的权限开关。
     */
    normalizeSessionBoolean(value) {
      return value === true || value === 1 || value === "1" || value === "true";
    },
    /**
     * 判断当前页面是否具备团队点单权限和必要上下文。
     * 除两个业务开关外额外要求 TeamId，避免配置开启但团队创建失败时误发消息。
     *
     * @returns {boolean} true 表示允许读写团队购物车。
     */
    isTeamOrderActive() {
      return Boolean(
        this.isGroupConfig && this.isSureVpn && this.teamId
      );
    },
    /**
     * 请求服务端返回当前团队的完整购物车。
     * WebSocket 首次连接成功时公共连接模块也会主动请求，此处用于复用已有连接。
     *
     * @returns {void}
     */
    requestTeamGoodsList() {
      if (!this.isTeamOrderActive()) return;
      handleSendMessage("SendGoodsList", {
        TeamId: this.teamId,
        duration: this.getTeamOrderDuration(),
      });
    },
    /**
     * 获取团队购物车有效时长。
     *
     * @returns {number|undefined} 门店配置中的团队点单有效时长。
     */
    getTeamOrderDuration() {
      const vpnInfo = handleSessionStorage.get("vpnInfo") || {};
      return vpnInfo.OrderTeamDuration || 0;
    },
    /**
     * 移除本页面注册的团队事件监听。
     * 传入具体回调，只移除黑色页面自己的监听，不影响原版点餐页面。
     *
     * @returns {void}
     */
    removeTeamOrderingListeners() {
      if (this.teamGoodsChangeHandler) {
        EventBus.$off("getMessage", this.teamGoodsChangeHandler);
        this.teamGoodsChangeHandler = null;
      }
      if (this.teamGoodsListHandler) {
        EventBus.$off("getGroupGoodsList", this.teamGoodsListHandler);
        this.teamGoodsListHandler = null;
      }
    },
    /**
     * 将团队接口的一条菜品转换为黑色页面购物车结构。
     *
     * @param {Object} goods 团队接口返回的菜品。
     * @param {number} quantity 需要写入的数量。
     * @returns {Object|null} 黑色购物车条目；数据无商品 id 时返回 null。
     */
    normalizeTeamCartItem(goods, quantity) {
      if (!goods || typeof goods !== "object") return null;
      const goodsId = goods.Id != null ? goods.Id : goods.ProductId;
      if (goodsId == null) return null;
      const isSetMeal = Number(goods.IsServiceId || goods.GoodsServiceId) === 6;
      const specId = isSetMeal
        ? "setmeal"
        : goods.SpecificId != null
          ? String(goods.SpecificId)
          : "default";
      const remarkText = String(
        goods.remark || goods.Remark || ""
      ).trim();
      const key =
        goods.BlackCartKey ||
        `${goodsId}_${specId}_${remarkText}`;
      const packageSelections = goods.PackageSelections || [];
      const menuGoods =
        goods.MenuGoods ||
        packageSelections.map((mealItem) => ({
          GoodsId: mealItem.goodsId,
          GoodsName: mealItem.goodsName,
          GoodsCode: mealItem.goodsCode,
          Price: mealItem.price,
          NumberCount: mealItem.numberCount || 1,
        }));
      return {
        key,
        goodsId,
        catalogId: goods.GoodsCatalogId,
        catalogName: goods.GoodsCatalog || goods.ProductType || "精选菜品",
        name: goods.GoodsName || goods.ProductName || "",
        image: goods.PicUrl || "",
        specId,
        specName: goods.Specification || (isSetMeal ? "套餐" : "标准份"),
        price: this.normalizePrice(goods.Price),
        remarkText,
        quantity: Math.max(0, Number(quantity) || 0),
        isSetMeal,
        packageSelections,
        sourceGoods: { ...goods, MenuGoods: menuGoods },
      };
    },
    /**
     * 使用服务端团队购物车替换本地购物车，并合并相同组合的数量。
     *
     * @param {Array<Object>} goodsList 团队购物车完整列表。
     * @returns {void}
     */
    replaceCartWithTeamGoods(goodsList) {
      if (!Array.isArray(goodsList)) return;
      const itemMap = new Map();
      goodsList.forEach((goods) => {
        const item = this.normalizeTeamCartItem(goods, goods.Quantity);
        if (!item || item.quantity <= 0) return;
        const existing = itemMap.get(item.key);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          itemMap.set(item.key, item);
        }
      });
      this.cartItems = Array.from(itemMap.values());
    },
    /**
     * 应用其他团队成员发来的单次数量变化。
     *
     * @param {Object} goods 包含 Quantity 正负变化量的团队消息。
     * @returns {void}
     */
    applyTeamGoodsChange(goods) {
      if (!goods || !Number(goods.Quantity)) return;
      const incoming = this.normalizeTeamCartItem(
        goods,
        Math.abs(Number(goods.Quantity))
      );
      if (!incoming) return;
      const index = this.cartItems.findIndex(
        (item) => item.key === incoming.key
      );
      const nextQuantity =
        (index >= 0 ? Number(this.cartItems[index].quantity) : 0) +
        Number(goods.Quantity);
      if (nextQuantity <= 0) {
        if (index >= 0) this.cartItems.splice(index, 1);
      } else if (index >= 0) {
        this.cartItems[index].quantity = Math.min(99, nextQuantity);
      } else {
        incoming.quantity = Math.min(99, nextQuantity);
        this.cartItems.push(incoming);
      }
    },
    /**
     * 将黑色购物车的数量变化转换成原团队点单协议并发送。
     *
     * @param {Object} item 黑色购物车条目。
     * @param {number} quantityChange 正数为增加，负数为减少。
     * @returns {void}
     */
    sendTeamGoodsChange(item, quantityChange) {
      if (!this.isTeamOrderActive() || !item || !quantityChange) return;
      const sourceGoods = item.sourceGoods || {};
      const isMultiSpecific =
        item.specId !== "default" && item.specId !== "setmeal";
      const params = {
        TeamId: this.teamId,
        Quantity: Number(quantityChange),
        TableCode:
          (handleSessionStorage.get("deskCode") || {}).code || "",
        VpnId: this.currentVpnId,
        duration: this.getTeamOrderDuration(),
        GoodsCatalog: item.catalogName,
        GoodsCatalogId: item.catalogId,
        GoodsName: item.name,
        Id: item.goodsId,
        IsMultiSpecific: isMultiSpecific,
        IsServiceId: item.isSetMeal ? 6 : sourceGoods.IsServiceId,
        PicUrl: item.image,
        Price: item.price,
        MinBuyCount: sourceGoods.MinBuyCount,
        remark: item.remarkText,
        BlackCartKey: item.key,
        PackageSelections: item.packageSelections || [],
        MenuGoods:
          sourceGoods.MenuGoods ||
          (item.packageSelections || []).map((mealItem) => ({
            GoodsId: mealItem.goodsId,
            GoodsName: mealItem.goodsName,
            GoodsCode: mealItem.goodsCode,
            Price: mealItem.price,
            NumberCount: mealItem.numberCount || 1,
          })),
      };
      if (isMultiSpecific) {
        params.SpecificId = item.specId;
        params.Specification = item.specName;
      }
      if (sourceGoods.MethodName || sourceGoods.DoWay) {
        params.DoWay = sourceGoods.MethodName || sourceGoods.DoWay;
      }
      if (sourceGoods.Taste) params.Taste = sourceGoods.Taste;
      handleSendMessage("SendChangeGoods", params);
    },
    /**
     * 通知团队服务端清空当前团队购物车。
     *
     * @returns {void}
     */
    sendTeamGoodsClear() {
      if (!this.isTeamOrderActive()) return;
      handleSendMessage("SendGoodsClear", {
        TeamId: this.teamId,
        duration: this.getTeamOrderDuration(),
      });
    },
    /**
     * 从不同版本的接口响应中提取分类数组。
     * 当前请求拦截器通常已经返回 Content，但这里同时兼容数组、Content、Data、
     * List、Items 以及小写字段，防止后端包装结构调整后页面直接变成空白。
     *
     * @param {Array|Object|null} response getFoodList 返回的原始响应。
     * @returns {Array<Object>} 提取出的分类原始数组；无法识别时返回空数组。
     */
    extractCategoryArray(response) {
      if (Array.isArray(response)) return response;
      if (!response || typeof response !== "object") return [];
      const candidates = [
        response.Content,
        response.content,
        response.Data,
        response.data,
        response.List,
        response.list,
        response.Items,
        response.items,
      ];
      return candidates.find((item) => Array.isArray(item)) || [];
    },
    /**
     * 将可能为数组、逗号字符串或 null 的接口字段转换成数组。
     * 口味、做法、备注字段在不同门店配置中可能返回不同类型，统一后便于模板遍历。
     *
     * @param {Array|string|null|undefined} value 接口原始值。
     * @returns {Array} 数组原样浅拷贝；字符串按中英文逗号拆分；其他类型返回空数组。
     */
    normalizeArray(value) {
      if (Array.isArray(value)) return value.slice();
      if (typeof value === "string") {
        return value
          .split(/[,，]/)
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return [];
    },
    /**
     * 将接口价格转换为可计算的有限数字。
     * 支持普通数字和带“¥”或逗号的字符串，非法值统一回退为 0，避免合计金额出现 NaN。
     *
     * @param {number|string|null|undefined} value 接口价格值。
     * @returns {number} 可安全参与计算的价格数字。
     */
    normalizePrice(value) {
      const price = Number(String(value == null ? "" : value).replace(/[¥,\s]/g, ""));
      return Number.isFinite(price) ? price : 0;
    },
    /**
     * 标准化单条规格数据，统一 id、名称和价格字段。
     *
     * @param {Object} specification 接口规格对象。
     * @param {number} index 规格在所属菜品中的序号，用于生成缺失 id。
     * @param {number} fallbackPrice 菜品基础价格，用于规格价格缺失时兜底。
     * @returns {Object} 页面内部统一使用的规格对象。
     */
    normalizeSpecification(specification, index, fallbackPrice) {
      const source = specification || {};
      const rawId =
        source.Id != null
          ? source.Id
          : source.id != null
            ? source.id
            : source.SpecificId != null
              ? source.SpecificId
              : `spec-${index}`;
      return {
        ...source,
        Id: String(rawId),
        Name:
          source.Name ||
          source.name ||
          source.SpecificName ||
          source.SpecificationName ||
          `规格${index + 1}`,
        Price: this.normalizePrice(
          source.Price != null ? source.Price : source.price != null ? source.price : fallbackPrice
        ),
      };
    },
    /**
     * 标准化菜品字段，并补充所属分类 id。
     * 兼容常见的大小写字段差异，同时确保规格、口味、做法和备注始终为数组。
     *
     * @param {Object} goods 接口菜品原始对象。
     * @param {string|number} catalogId 所属分类 id。
     * @param {number} index 菜品在分类中的序号，用于生成缺失 id。
     * @returns {Object} 页面、规格弹窗和购物车共同使用的标准化菜品对象。
     */
    normalizeGoods(goods, catalogId, index) {
      const source = goods || {};
      const goodsId =
        source.Id != null
          ? source.Id
          : source.id != null
            ? source.id
            : source.GoodsCode || source.goodsCode || `${catalogId}-goods-${index}`;
      const price = this.normalizePrice(
        source.Price != null ? source.Price : source.price
      );
      const specifications = this.normalizeArray(
        source.Specifications || source.specifications
      ).map((item, specificationIndex) =>
        this.normalizeSpecification(item, specificationIndex, price)
      );
      return {
        ...source,
        Id: goodsId,
        GoodsCode: source.GoodsCode || source.goodsCode || String(goodsId),
        GoodsCatalogId: catalogId,
        GoodsName: source.GoodsName || source.goodsName || source.Name || "未命名菜品",
        Description: source.Description || source.description || source.Introduction || "",
        PicUrl: source.PicUrl || source.picUrl || source.ImageUrl || source.imageUrl || "",
        Unit: source.Unit || source.unit || "份",
        Price: price,
        IsServiceId:
          source.IsServiceId != null
            ? source.IsServiceId
            : source.isServiceId != null
              ? source.isServiceId
              : source.ServiceId,
        MinBuyCount: Math.max(
          1,
          parseInt(
            source.MinBuyCount != null ? source.MinBuyCount : source.minBuyCount,
            10
          ) || 1
        ),
        IsRecommend: Boolean(source.IsRecommend || source.isRecommend),
        IsMultiSpecific: Boolean(
          source.IsMultiSpecific || source.isMultiSpecific || specifications.length > 1
        ),
        Specifications: specifications,
        TasteArray: this.normalizeArray(source.TasteArray || source.tasteArray),
        MethodArray: this.normalizeArray(source.MethodArray || source.methodArray),
        GoodsRemarkArray: this.normalizeArray(
          source.GoodsRemarkArray || source.goodsRemarkArray
        ),
      };
    },
    /**
     * 标准化完整分类列表，过滤无效分类并统一分类与菜品字段。
     *
     * @param {Array<Object>} categoryArray 接口分类原始数组。
     * @returns {Array<Object>} BlackMicropoint 页面内部使用的分类列表。
     */
    normalizeCategoryGroups(categoryArray) {
      return categoryArray
        .filter((group) => group && typeof group === "object")
        .map((group, index) => {
          const catalogId =
            group.GoodsCatalogId != null
              ? group.GoodsCatalogId
              : group.id != null
                ? group.id
                : `catalog-${index}`;
          const goodsArray = this.normalizeArray(
            group.Goods || group.goods || group.Items || group.items
          );
          return {
            ...group,
            GoodsCatalogId: catalogId,
            GoodsCatalogName:
              group.GoodsCatalogName || group.goodsCatalogName || group.Name || `分类${index + 1}`,
            Goods: goodsArray.map((goods, goodsIndex) =>
              this.normalizeGoods(goods, catalogId, goodsIndex)
            ),
          };
        });
    },
    /**
     * 调用 getFoodList 获取动态分类和菜品。
     * 每次请求分配递增序号，仅最后一次请求允许更新页面，避免门店快速切换时旧请求覆盖新数据。
     * 返回结果先经过提取和标准化；接口失败时保留空列表并提示用户。
     *
     * @param {Object} params getFoodList 接口参数。
     * @param {string} params.code SaaS/门店编码，接口路径必填。
     * @param {string|number} params.diningRoomId 餐厅或区域 id。
     * @param {string|number} params.vpnId 门店 id。
     * @returns {Promise<void>} 数据装载结束后完成；错误已在方法内部处理。
     */
    async loadFoodList(params) {
      if (!params.code) {
        this.categoryGroups = [];
        this.activeCategoryId = null;
        this.loading = false;
        return;
      }

      const currentRequest = ++this.requestSequence;
      this.loading = true;
      try {
        const response = await getFoodList(params);
        if (currentRequest !== this.requestSequence) return;
        const categoryArray = this.extractCategoryArray(response);
        this.categoryGroups = this.normalizeCategoryGroups(categoryArray);
        this.activeCategoryId = this.categoryGroups.length
          ? this.categoryGroups[0].GoodsCatalogId
          : null;
      } catch (error) {
        if (currentRequest !== this.requestSequence) return;
        this.categoryGroups = [];
        this.activeCategoryId = null;
        this.$toast("菜品加载失败，请稍后重试");
      } finally {
        if (currentRequest === this.requestSequence) {
          this.loading = false;
        }
      }
    },
    /**
     * 切换左侧菜品分类。
     * 切换分类时清空搜索关键词，避免用户误以为目标分类没有菜品。
     *
     * @param {string|number} categoryId 分类唯一标识。
     * @returns {void}
     */
    selectCategory(categoryId) {
      this.searchKeyword = "";
      this.searchFocused = false;
      this.activeCategoryId = categoryId;
    },
    clearSearchKeyword() {
      this.searchKeyword = "";
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
        this.searchBlurTimer = null;
      }
      this.searchFocused = true;
      this.$nextTick(() => {
        this.focusSearchInput();
      });
    },
    focusSearchInput() {
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
        this.searchBlurTimer = null;
      }
      this.searchFocused = true;
      const input = this.$refs.searchInput;
      if (input && document.activeElement !== input) {
        input.focus();
      }
    },
    handleSearchFocus() {
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
        this.searchBlurTimer = null;
      }
      this.searchFocused = true;
    },
    handleSearchBlur(e) {
      const relatedTarget = e && e.relatedTarget;
      const input = this.$refs.searchInput;
      const searchRoot = input && input.closest(".search-box, .bv2-search-inner");
      if (relatedTarget && searchRoot && searchRoot.contains(relatedTarget)) {
        return;
      }
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
      }
      this.searchBlurTimer = window.setTimeout(() => {
        if (input && document.activeElement === input) {
          this.searchBlurTimer = null;
          return;
        }
        this.searchFocused = false;
        this.searchBlurTimer = null;
      }, 200);
    },
    /**
     * 判断菜品是否需要打开新规格弹窗。
     * 多规格、口味、做法或预设备注任意一项存在时均进入规格选择流程。
     *
     * @param {Object|null} goods 标准化后的菜品对象。
     * @returns {boolean} true 表示必须进入规格/备注选择流程。
     */
    needsSpecification(goods) {
      if (!goods) return false;
      return Boolean(
        goods.IsMultiSpecific ||
        (goods.Specifications && goods.Specifications.length) ||
        (goods.TasteArray && goods.TasteArray.length) ||
        (goods.MethodArray && goods.MethodArray.length) ||
        (goods.GoodsRemarkArray && goods.GoodsRemarkArray.length)
      );
    },
    /**
     * 判断菜品是否为套餐。
     * 与 Micropoint.vue 的套餐判断保持一致：后端 IsServiceId 等于 6 时进入套餐流程。
     * 同时兼容接口可能返回数字字符串 "6"，避免严格类型差异导致套餐被当作普通菜品。
     *
     * @param {Object|null} goods 标准化后的菜品对象。
     * @returns {boolean} true 表示点击添加时必须先打开套餐选择弹窗。
     */
    isSetMeal(goods) {
      return Boolean(goods && Number(goods.IsServiceId) === 6);
    },
    /**
     * 判断菜品是否已完全售罄。
     *
     * @param {Object|null} goods 菜品对象。
     * @returns {boolean} 开启沽清且剩余数量为 0 时返回 true。
     */
    isFullySoldOut(goods) {
      return Boolean(
        goods &&
        [true, 1, "1"].includes(goods.IsSellOut) &&
        Number(goods.SellOutNumber) === 0
      );
    },
    /**
     * 判断本次增加数量是否会超过菜品沽清数量。
     *
     * @param {Object|null} goods 菜品对象。
     * @param {number} currentQuantity 当前数量。
     * @param {number} addQuantity 本次增加数量。
     * @returns {boolean} 超过沽清数量时返回 true。
     */
    exceedsSellOutNumber(goods, currentQuantity, addQuantity) {
      if (
        !goods ||
        ![true, 1, "1"].includes(goods.IsSellOut) ||
        Number(goods.SellOutNumber) === 0
      ) {
        return false;
      }
      return (
        Number(currentQuantity || 0) + Number(addQuantity || 0) >
        Number(goods.SellOutNumber)
      );
    },
    showSellOutLimitToast() {
      this.$toast("添加数量超过餐品沽清数量，无法添加哦");
    },
    /**
     * 获取菜品展示价格；多规格菜品显示最低规格价。
     * 会排除非法规格价格，保证界面不会展示 NaN。
     *
     * @param {Object|null} goods 标准化后的菜品对象。
     * @returns {number} 菜品基础价格或最低规格价格。
     */
    getGoodsPrice(goods) {
      if (!goods) return 0;
      const prices = (goods.Specifications || [])
        .map((item) => this.normalizePrice(item.Price))
        .filter((price) => Number.isFinite(price));
      return prices.length ? Math.min(...prices) : this.normalizePrice(goods.Price);
    },
    /**
     * 打开本页面独立的菜品详情弹窗。
     * 只写入详情弹窗自己的状态，不复用规格或购物车弹窗变量，避免互相影响。
     *
     * @param {Object} goods 需要查看的菜品对象。
     */
    openDetail(goods) {
      if (!goods || this.isFullySoldOut(goods)) return;
      this.showCartPopup = false;
      this.showSpecificationPopup = false;
      this.showSetMealPopup = false;
      this.detailGoods = goods;
      this.detailMinimumQuantity = Math.max(
        1,
        parseInt(goods.MinBuyCount, 10) || 1
      );
      this.detailQuantity = this.detailMinimumQuantity;
      this.showDetailPopup = true;
    },
    /**
     * 修改菜品详情弹窗中的购买数量。
     * 增加时按菜品 MinBuyCount 步进，减少时仍减 1 份，最大数量为 99；
     * 该数量会在点击加入时继续传递给普通菜品、规格弹窗或套餐弹窗。
     *
     * @param {number} direction 1 表示增加，-1 表示减少。
     * @returns {void}
     */
    changeDetailQuantity(direction) {
      const step = Number(direction);
      if (!Number.isFinite(step) || step === 0) return;
      const addStep = this.detailMinimumQuantity;
      if (
        step > 0 &&
        this.exceedsSellOutNumber(
          this.detailGoods,
          this.detailQuantity,
          addStep
        )
      ) {
        this.showSellOutLimitToast();
        return;
      }
      const nextQuantity =
        this.detailQuantity + (step > 0 ? addStep : -1);
      if (nextQuantity < this.detailMinimumQuantity) {
        this.$toast(`该菜品 ${this.detailMinimumQuantity} 份起购`);
        return;
      }
      if (nextQuantity > 99) {
        this.$toast("单个菜品最多选择 99 份");
        return;
      }
      this.detailQuantity = nextQuantity;
    },
    /**
     * 触发关闭详情弹窗。
     * 临时菜品引用由 closed 事件统一清理，确保关闭动画期间模板仍有完整数据。
     *
     * @returns {void}
     */
    closeDetail() {
      this.showDetailPopup = false;
    },
    /**
     * 详情弹窗完全关闭后的清理回调。
     * 同时兼容按钮关闭、遮罩关闭和下滑关闭三种方式。
     *
     * @returns {void}
     */
    resetDetailPopup() {
      this.detailGoods = null;
      this.detailQuantity = 1;
      this.detailMinimumQuantity = 1;
    },
    /**
     * 从详情弹窗加入菜品。
     * 有规格的菜品先关闭详情再打开独立规格弹窗；普通菜品直接加入购物车。
     *
     * @returns {void}
     */
    addDetailGoods() {
      if (!this.detailGoods) return;
      const goods = this.detailGoods;
      const quantity = this.detailQuantity;
      this.closeDetail();
      if (this.isSetMeal(goods)) {
        this.$nextTick(() => this.openSetMeal(goods, null, quantity));
      } else if (this.needsSpecification(goods)) {
        this.$nextTick(() => this.openSpecification(goods, quantity));
      } else if (goods.Id || goods.GoodsCode) {
        this.addSimpleGoods(goods, quantity);
      }
    },
    /**
     * 打开独立规格弹窗并重置上一次编辑内容。
     * 打开前关闭详情和购物车弹窗，保证任意时刻只显示一个底部弹层。
     *
     * @param {Object} goods 需要选择规格的标准化菜品对象。
     * @param {number} initialQuantity 从详情弹窗传入的待加入数量，默认 1。
     * @returns {void}
     */
    openSpecification(goods, initialQuantity = 1) {
      if (!goods) return;
      this.showDetailPopup = false;
      this.showCartPopup = false;
      this.showSetMealPopup = false;
      this.editingGoods = goods;
      this.selectedRemarks = [];
      this.customRemark = "";
      this.specificationAddQuantity = Math.min(
        99,
        Math.max(1, Number(initialQuantity) || 1)
      );
      const specifications = goods.Specifications || [];
      this.selectedSpecificationId = specifications.length
        ? String(specifications[0].Id)
        : "default";
      this.showSpecificationPopup = true;
    },
    /**
     * 触发关闭规格弹窗。
     * 编辑数据在关闭动画完成后通过 resetSpecificationPopup 清理。
     *
     * @returns {void}
     */
    closeSpecification() {
      this.showSpecificationPopup = false;
    },
    /**
     * 规格弹窗完全关闭后的状态清理回调。
     * 遮罩关闭或手势关闭也会执行，避免下次打开残留上一次备注。
     *
     * @returns {void}
     */
    resetSpecificationPopup() {
      this.editingGoods = null;
      this.selectedSpecificationId = null;
      this.selectedRemarks = [];
      this.customRemark = "";
      this.specificationAddQuantity = 1;
    },
    /**
     * 从套餐接口响应中提取 Groups 数组。
     * getSetmealFoodInfo 通常直接返回包含 Groups 的对象，同时兼容 Content/Data 包装。
     *
     * @param {Object|null} response 套餐详情接口原始响应。
     * @returns {Object} 套餐主体对象；无法识别时返回空对象。
     */
    extractSetMealInfo(response) {
      if (!response || typeof response !== "object") return {};
      if (Array.isArray(response.Groups) || Array.isArray(response.groups)) {
        return response;
      }
      const wrappers = [
        response.Content,
        response.content,
        response.Data,
        response.data,
      ];
      return (
        wrappers.find(
          (item) =>
            item &&
            typeof item === "object" &&
            (Array.isArray(item.Groups) || Array.isArray(item.groups))
        ) || {}
      );
    },
    /**
     * 将套餐接口的 Groups/Items 转成弹窗可直接操作的数据。
     * 参考 Micropoint.vue：新增套餐时，每组默认选中前 SelectRows 项；
     * 编辑购物车套餐时，则根据购物车中保存的 packageSelections 恢复选择。
     *
     * @param {Object} setMealInfo 套餐接口主体对象。
     * @param {Array<Object>} previousSelections 编辑时原购物车保存的选择项。
     * @returns {Array<Object>} 包含组规则和选中状态的套餐分组。
     */
    normalizeSetMealGroups(setMealInfo, previousSelections) {
      const groups = this.normalizeArray(
        setMealInfo.Groups || setMealInfo.groups
      );
      const previousSelectionMap = new Map(
        (previousSelections || []).map((item) => [
          String(item.slotKey || item.selectionKey),
          item,
        ])
      );
      return groups.map((group, groupIndex) => {
        const groupId =
          group.Id != null
            ? group.Id
            : group.id != null
              ? group.id
              : `setmeal-group-${groupIndex}`;
        const selectRows = Math.max(
          0,
          parseInt(group.SelectRows != null ? group.SelectRows : group.selectRows, 10) || 0
        );
        const items = this.normalizeArray(group.Items || group.items).map(
          (item, itemIndex) => {
            const goodsId =
              item.GoodsId != null
                ? item.GoodsId
                : item.Id != null
                  ? item.Id
                  : item.id != null
                    ? item.id
                    : `${groupId}-item-${itemIndex}`;
            const slotKey = `${groupId}_${goodsId}`;
            const previousSelection = previousSelectionMap.get(slotKey);
            const hasPreviousSelection = previousSelectionMap.size > 0;
            const displayedGoodsId = previousSelection
              ? previousSelection.goodsId
              : goodsId;
            return {
              ...item,
              GoodsId: displayedGoodsId,
              GoodsName: previousSelection
                ? previousSelection.goodsName
                : item.GoodsName ||
                item.goodsName ||
                item.Name ||
                `套餐菜品${itemIndex + 1}`,
              Description: previousSelection
                ? previousSelection.description || ""
                : item.Description || item.description || "",
              PicUrl: previousSelection
                ? previousSelection.picUrl || ""
                : item.PicUrl || item.picUrl || item.ImageUrl || "",
              Unit: previousSelection
                ? previousSelection.unit || "份"
                : item.Unit || item.unit || "份",
              Price: previousSelection
                ? this.normalizePrice(previousSelection.price)
                : this.normalizePrice(
                  item.Price != null ? item.Price : item.price
                ),
              GoodsChangeItems: this.normalizeArray(
                item.GoodsChangeItems || item.goodsChangeItems
              ),
              groupId,
              slotKey,
              selectionKey: `${groupId}_${displayedGoodsId}`,
              selected: hasPreviousSelection
                ? Boolean(previousSelection)
                : itemIndex < selectRows,
            };
          }
        );
        return {
          id: groupId,
          name: group.GroupName || group.groupName || group.Name || `套餐分组${groupIndex + 1}`,
          selectRows: Math.min(selectRows, items.length),
          items,
        };
      });
    },
    /**
     * 打开独立套餐选择弹窗并请求套餐明细。
     * 新增时使用 Micropoint.vue 的默认选中规则；从购物车编辑时恢复原套餐组合。
     * 请求使用递增序号防止快速切换套餐时旧响应覆盖当前弹窗。
     *
     * @param {Object} goods 套餐商品对象。
     * @param {Object|null} cartItem 需要编辑的购物车套餐条目；新增时不传。
     * @param {number|null} initialQuantity 从详情弹窗传入的初始套餐数量。
     * @returns {Promise<void>} 套餐接口加载并完成弹窗初始化后结束。
     */
    async openSetMeal(goods, cartItem = null, initialQuantity = null) {
      if (!this.isSetMeal(goods)) return;
      this.showDetailPopup = false;
      this.showSpecificationPopup = false;
      this.showCartPopup = false;
      this.editingSetMealGoods = goods;
      this.editingSetMealCartKey = cartItem ? cartItem.key : null;
      this.setMealMinimumQuantity = Math.max(
        1,
        parseInt(goods.MinBuyCount, 10) || 1
      );
      this.setMealQuantity = cartItem
        ? Math.max(this.setMealMinimumQuantity, Number(cartItem.quantity) || 1)
        : Math.max(
          this.setMealMinimumQuantity,
          Number(initialQuantity) || this.setMealMinimumQuantity
        );
      this.setMealInfo = {};
      this.setMealGroups = [];
      this.setMealLoading = true;
      this.showSetMealPopup = true;

      const currentRequest = ++this.setMealRequestSequence;
      try {
        const response = await getSetmealFoodInfo({
          goodsId: goods.Id,
          vpnId: this.currentVpnId,
        });
        if (currentRequest !== this.setMealRequestSequence) return;
        const setMealInfo = this.extractSetMealInfo(response);
        this.setMealInfo = {
          ...setMealInfo,
          GoodsName: setMealInfo.GoodsName || goods.GoodsName,
        };
        this.setMealMinimumQuantity = Math.max(
          1,
          parseInt(
            setMealInfo.MinBuyCount != null
              ? setMealInfo.MinBuyCount
              : goods.MinBuyCount,
            10
          ) || 1
        );
        this.setMealQuantity = Math.min(
          99,
          Math.max(this.setMealMinimumQuantity, this.setMealQuantity)
        );
        this.setMealGroups = this.normalizeSetMealGroups(
          setMealInfo,
          cartItem ? cartItem.packageSelections : []
        );
      } catch (error) {
        if (currentRequest !== this.setMealRequestSequence) return;
        this.setMealInfo = {};
        this.setMealGroups = [];
        this.$toast("套餐内容加载失败，请稍后重试");
      } finally {
        if (currentRequest === this.setMealRequestSequence) {
          this.setMealLoading = false;
        }
      }
    },
    /**
     * 获取某一套餐分组当前已选项数量。
     *
     * @param {Object} group 标准化套餐分组。
     * @returns {number} group.items 中 selected 为 true 的数量。
     */
    getSetMealGroupSelectedCount(group) {
      return (group.items || []).filter((item) => item.selected).length;
    },
    /**
     * 修改套餐弹窗中的购买数量。
     * 增加时按套餐 MinBuyCount 步进；减少时仍减 1 份且不得低于起购数量，
     * 增加时最多选择 99 份，避免购物车数量和角标展示溢出。
     *
     * @param {number} direction 1 表示增加一份，-1 表示减少一份。
     * @returns {void}
     */
    changeSetMealQuantity(direction) {
      const step = Number(direction);
      if (!Number.isFinite(step) || step === 0) return;
      const addStep = this.setMealMinimumQuantity;
      if (
        step > 0 &&
        this.exceedsSellOutNumber(
          this.editingSetMealGoods,
          this.setMealQuantity,
          addStep
        )
      ) {
        this.showSellOutLimitToast();
        return;
      }
      const nextQuantity =
        this.setMealQuantity + (step > 0 ? addStep : -1);
      if (nextQuantity < this.setMealMinimumQuantity) {
        this.$toast(`该套餐 ${this.setMealMinimumQuantity} 份起购`);
        return;
      }
      if (nextQuantity > 99) {
        this.$toast("单个套餐最多选择 99 份");
        return;
      }
      this.setMealQuantity = nextQuantity;
    },
    /**
     * 判断套餐明细是否配置了可更换菜品。
     * 仅当 GoodsChangeItems 是非空数组时显示“更换菜品”按钮；
     * 未配置可换菜品的固定套餐明细右侧不渲染任何图标或按钮。
     *
     * @param {Object|null} item 套餐明细对象。
     * @returns {boolean} true 表示允许进入更换菜品流程。
     */
    hasSetMealChangeItems(item) {
      return Boolean(
        item &&
        Array.isArray(item.GoodsChangeItems) &&
        item.GoodsChangeItems.length
      );
    },
    /**
     * 打开独立的可换菜品弹窗，并标准化 Micropoint.vue 使用的 DesGoodsId、
     * DesGoodsName 等目标菜品字段。原套餐弹窗保持打开，关闭更换弹窗后可继续确认套餐。
     *
     * @param {Object} item 需要更换的套餐明细。
     * @returns {void}
     */
    openSetMealChange(item) {
      if (!this.hasSetMealChangeItems(item)) return;
      this.changingSetMealItem = item;
      this.setMealChangeOptions = item.GoodsChangeItems.map(
        (changeItem, index) => {
          const id =
            changeItem.DesGoodsId != null
              ? changeItem.DesGoodsId
              : changeItem.GoodsId != null
                ? changeItem.GoodsId
                : changeItem.Id != null
                  ? changeItem.Id
                  : `change-${index}`;
          return {
            ...changeItem,
            id: String(id),
            GoodsId: id,
            GoodsName:
              changeItem.DesGoodsName ||
              changeItem.GoodsName ||
              changeItem.Name ||
              `可换菜品${index + 1}`,
            Description: changeItem.Description || changeItem.description || "",
            PicUrl:
              changeItem.PicUrl || changeItem.picUrl || changeItem.ImageUrl || "",
            Unit: changeItem.Unit || changeItem.unit || "份",
            Price: this.normalizePrice(
              changeItem.Price != null ? changeItem.Price : changeItem.price
            ),
          };
        }
      );
      this.selectedSetMealChangeId = null;
      this.showSetMealChangePopup = true;
    },
    /**
     * 选择一项目标菜品。这里只记录选择，不立即改写套餐明细，
     * 用户点击“取消”时原菜品能够保持不变。
     *
     * @param {Object} item 标准化后的可换菜品。
     * @returns {void}
     */
    selectSetMealChange(item) {
      this.selectedSetMealChangeId = item ? item.id : null;
    },
    /**
     * 确认更换菜品。
     * 保留原明细的 GoodsChangeItems 和 slotKey，以便用户可以再次更换；
     * selectionKey 更新为目标菜品 id，使不同套餐组合能生成不同购物车条目。
     *
     * @returns {void}
     */
    confirmSetMealChange() {
      if (!this.changingSetMealItem || !this.selectedSetMealChangeId) return;
      const selectedItem = this.setMealChangeOptions.find(
        (item) => item.id === this.selectedSetMealChangeId
      );
      if (!selectedItem) return;
      Object.assign(this.changingSetMealItem, {
        GoodsId: selectedItem.GoodsId,
        GoodsName: selectedItem.GoodsName,
        Description: selectedItem.Description,
        PicUrl: selectedItem.PicUrl,
        Unit: selectedItem.Unit,
        Price: selectedItem.Price,
        selectionKey: `${this.changingSetMealItem.groupId}_${selectedItem.GoodsId}`,
      });
      this.closeSetMealChange();
    },
    /**
     * 关闭更换菜品弹窗，不修改当前套餐明细。
     *
     * @returns {void}
     */
    closeSetMealChange() {
      this.showSetMealChangePopup = false;
    },
    /**
     * 更换菜品弹窗完全关闭后的临时状态清理。
     *
     * @returns {void}
     */
    resetSetMealChangePopup() {
      this.changingSetMealItem = null;
      this.setMealChangeOptions = [];
      this.selectedSetMealChangeId = null;
    },
    /**
     * 校验所有套餐分组是否达到 SelectRows 要求。
     *
     * @returns {Object|null} 第一个未满足数量要求的分组；全部有效时返回 null。
     */
    getInvalidSetMealGroup() {
      return (
        this.setMealGroups.find(
          (group) =>
            this.getSetMealGroupSelectedCount(group) !== group.selectRows
        ) || null
      );
    },
    /**
     * 将当前套餐选择扁平化为购物车可保存的数据，并生成用户可读摘要。
     *
     * @returns {{selections: Array<Object>, summary: string}} 套餐选择明细和展示摘要。
     */
    buildSetMealSelection() {
      const selections = [];
      const summaryParts = [];
      this.setMealGroups.forEach((group) => {
        const selectedItems = group.items.filter((item) => item.selected);
        summaryParts.push(
          `${group.name}：${selectedItems.map((item) => item.GoodsName).join("、")}`
        );
        selectedItems.forEach((item) => {
          selections.push({
            groupId: group.id,
            groupName: group.name,
            goodsId: item.GoodsId,
            goodsName: item.GoodsName,
            goodsCode: item.GoodsCode,
            description: item.Description,
            picUrl: item.PicUrl,
            unit: item.Unit,
            price: item.Price,
            slotKey: item.slotKey,
            selectionKey: item.selectionKey,
          });
        });
      });
      return {
        selections,
        summary: summaryParts.join("；"),
      };
    },
    /**
     * 确认套餐选择并新增或更新购物车条目。
     * 套餐唯一键由商品 id 和已选菜品 key 排序后组成，因此相同套餐组合会累加数量，
     * 不同组合会作为独立购物车条目；编辑后若与现有组合相同则自动合并。
     *
     * @returns {void}
     */
    confirmSetMeal() {
      if (!this.editingSetMealGoods || this.setMealLoading) return;
      const invalidGroup = this.getInvalidSetMealGroup();
      if (invalidGroup) {
        this.$toast(
          `“${invalidGroup.name}”需要选择 ${invalidGroup.selectRows} 项`
        );
        return;
      }

      const { selections, summary } = this.buildSetMealSelection();
      const goods = this.editingSetMealGoods;
      const selectionIdentity = selections
        .map((item) => item.selectionKey)
        .sort()
        .join("|");
      const goodsId = goods.Id || goods.GoodsCode;
      const key = `${goodsId}_setmeal_${selectionIdentity}`;
      const editingIndex = this.editingSetMealCartKey
        ? this.cartItems.findIndex(
          (item) => item.key === this.editingSetMealCartKey
        )
        : -1;
      const editingItem =
        editingIndex >= 0 ? this.cartItems[editingIndex] : null;
      const previousEditingItem = editingItem
        ? { ...editingItem }
        : null;
      const selectedQuantity = Math.min(
        99,
        Math.max(this.setMealMinimumQuantity, Number(this.setMealQuantity) || 1)
      );

      if (editingIndex >= 0) {
        this.cartItems.splice(editingIndex, 1);
      }
      const existingItem = this.cartItems.find((item) => item.key === key);
      if (existingItem) {
        if (
          this.exceedsSellOutNumber(
            goods,
            existingItem.quantity,
            selectedQuantity
          )
        ) {
          this.showSellOutLimitToast();
          if (editingItem) {
            this.cartItems.splice(editingIndex, 0, editingItem);
          }
          return;
        }
        const mergedQuantity = existingItem.quantity + selectedQuantity;
        if (mergedQuantity > 99) {
          this.$toast("相同套餐组合合计不能超过 99 份");
          if (editingItem) {
            this.cartItems.splice(editingIndex, 0, editingItem);
          }
          return;
        }
        existingItem.quantity = mergedQuantity;
      } else {
        this.cartItems.push({
          key,
          goodsId,
          catalogId: goods.GoodsCatalogId,
          catalogName: this.getCategoryNameById(goods.GoodsCatalogId),
          name: goods.GoodsName,
          image: goods.PicUrl,
          specId: "setmeal",
          specName: summary,
          price: this.getGoodsPrice(goods),
          remarkText: "",
          quantity: selectedQuantity,
          isSetMeal: true,
          packageSelections: selections,
          sourceGoods: { ...goods },
        });
      }
      if (previousEditingItem) {
        this.sendTeamGoodsChange(
          previousEditingItem,
          -Number(previousEditingItem.quantity)
        );
      }
      const savedItem = this.cartItems.find((item) => item.key === key);
      this.sendTeamGoodsChange(savedItem, selectedQuantity);
      this.closeSetMeal();
    },
    /**
     * 触发关闭套餐弹窗。
     * 套餐临时数据由 closed 回调清理，保证关闭动画期间内容不闪空。
     *
     * @returns {void}
     */
    closeSetMeal() {
      this.showSetMealChangePopup = false;
      this.showSetMealPopup = false;
    },
    /**
     * 套餐弹窗完全关闭后的清理回调。
     * 递增请求序号会使尚未返回的旧请求失效，不再更新已关闭弹窗。
     *
     * @returns {void}
     */
    resetSetMealPopup() {
      this.setMealRequestSequence += 1;
      this.showSetMealChangePopup = false;
      this.resetSetMealChangePopup();
      this.setMealLoading = false;
      this.editingSetMealGoods = null;
      this.editingSetMealCartKey = null;
      this.setMealInfo = {};
      this.setMealGroups = [];
      this.setMealQuantity = 1;
      this.setMealMinimumQuantity = 1;
    },
    /**
     * 切换一条预设备注，可同时选择多条。
     * 再次点击已选项时取消选择；空备注不会写入数组。
     *
     * @param {string} remark 用户点击的预设备注。
     * @returns {void}
     */
    toggleRemark(remark) {
      if (!remark) return;
      const index = this.selectedRemarks.indexOf(remark);
      if (index >= 0) {
        this.selectedRemarks.splice(index, 1);
      } else {
        this.selectedRemarks.push(remark);
      }
    },
    /**
     * 将规格弹窗中的选择合并为购物车条目。
     * 相同菜品、规格和备注生成同一 key 并累加数量，不同备注会成为独立条目。
     * 若规格在确认前被接口数据移除，则提示用户重新选择，不写入错误条目。
     *
     * @returns {void}
     */
    confirmSpecification() {
      if (!this.editingGoods || !this.selectedSpecificationId) return;
      const specification = this.specificationOptions.find(
        (item) => item.id === this.selectedSpecificationId
      );
      if (!specification) {
        this.$toast("当前规格不可用，请重新选择");
        return;
      }
      const remarkText = [...this.selectedRemarks, this.customRemark]
        .filter(Boolean)
        .join("、");
      this.addCartItem(
        this.editingGoods,
        specification,
        remarkText,
        this.specificationAddQuantity
      );
      this.closeSpecification();
    },
    /**
     * 为无规格菜品直接生成标准份购物车条目。
     *
     * @param {Object} goods 标准化后的菜品对象。
     * @param {number|null} quantity 本次加入数量；未传时按 MinBuyCount 加入。
     * @returns {void}
     */
    addSimpleGoods(goods, quantity = null) {
      const addQuantity =
        quantity == null
          ? Math.max(1, parseInt(goods.MinBuyCount, 10) || 1)
          : quantity;
      this.addCartItem(
        goods,
        { id: "default", name: "标准份", price: this.normalizePrice(goods.Price) },
        "",
        addQuantity
      );
    },
    /**
     * 获取普通单规格菜品当前在购物车中的数量。
     *
     * @param {Object} goods 接口菜品对象。
     * @returns {number} 该菜品标准份条目的数量。
     */
    getSimpleGoodsQuantity(goods) {
      const goodsId = goods.Id || goods.GoodsCode;
      const item = this.cartItems.find(
        (cartItem) =>
          cartItem.goodsId === goodsId && cartItem.specId === "default"
      );
      return item ? item.quantity : 0;
    },
    /**
     * 在菜品卡片上直接增加或减少普通菜品数量。
     * 增加时复用加入购物车逻辑；减少时复用购物车数量修改逻辑，减至零自动移除。
     *
     * @param {Object} goods 接口菜品对象。
     * @param {number} direction 1 表示增加，-1 表示减少。
     */
    changeSimpleGoodsQuantity(goods, direction) {
      const goodsId = goods.Id || goods.GoodsCode;
      const item = this.cartItems.find(
        (cartItem) =>
          cartItem.goodsId === goodsId && cartItem.specId === "default"
      );
      if (direction > 0) {
        const minBuyCount = Math.max(1, parseInt(goods.MinBuyCount, 10) || 1);
        if (
          this.exceedsSellOutNumber(
            goods,
            item ? item.quantity : 0,
            minBuyCount
          )
        ) {
          this.showSellOutLimitToast();
          return;
        }
        this.addSimpleGoods(goods, minBuyCount);
      } else if (item) {
        this.changeCartQuantity(item.key, -1);
      }
    },
    /**
     * 新增或累加购物车条目。
     *
     * @param {Object} goods 接口菜品对象。
     * @param {Object} specification 已选规格。
     * @param {string} remarkText 合并后的备注。
     * @param {number} quantity 本次需要加入的数量，默认 1。
     * @returns {void}
     */
    addCartItem(goods, specification, remarkText, quantity = 1) {
      if (!goods || !specification) return;
      const goodsId = goods.Id || goods.GoodsCode;
      if (goodsId == null) return;
      const addQuantity = Math.min(99, Math.max(1, Number(quantity) || 1));
      const key = `${goodsId}_${specification.id}_${remarkText}`;
      const existingItem = this.cartItems.find((item) => item.key === key);
      if (
        this.exceedsSellOutNumber(
          goods,
          existingItem ? existingItem.quantity : 0,
          addQuantity
        )
      ) {
        this.showSellOutLimitToast();
        return;
      }
      if (existingItem) {
        if (existingItem.quantity + addQuantity > 99) {
          this.$toast("单个菜品最多选择 99 份");
          return;
        }
        existingItem.quantity += addQuantity;
        this.sendTeamGoodsChange(existingItem, addQuantity);
        return;
      }
      const cartItem = {
        key,
        goodsId,
        catalogId: goods.GoodsCatalogId,
        catalogName: this.getCategoryNameById(goods.GoodsCatalogId),
        name: goods.GoodsName,
        image: goods.PicUrl,
        specId: specification.id,
        specName: specification.name,
        price: this.normalizePrice(specification.price),
        remarkText,
        quantity: addQuantity,
        sourceGoods: { ...goods },
      };
      this.cartItems.push(cartItem);
      this.sendTeamGoodsChange(cartItem, addQuantity);
    },
    /**
     * 根据分类 id 获取分类名称，供跨页面购物车展示和提交参数使用。
     *
     * @param {string|number} catalogId 菜品所属分类 id。
     * @returns {string} 接口分类名称；未找到时返回“精选菜品”。
     */
    getCategoryNameById(catalogId) {
      const group = this.categoryGroups.find(
        (item) => item.GoodsCatalogId === catalogId
      );
      return group ? group.GoodsCatalogName : "精选菜品";
    },
    /**
     * 修改购物车条目数量；数量减到 0 时直接移除该条目。
     * direction 可传任意正负数，但最终数量限制在 0～99，防止连续点击产生异常值。
     *
     * @param {string} key 购物车条目的组合唯一键。
     * @param {number} direction 本次数量变化值，通常为 1 或 -1。
     * @returns {void}
     */
    changeCartQuantity(key, direction) {
      const index = this.cartItems.findIndex((item) => item.key === key);
      if (index < 0) return;
      const directionStep = Number(direction);
      if (!Number.isFinite(directionStep) || directionStep === 0) return;
      const changedItem = this.cartItems[index];
      let quantityChange = directionStep;
      if (directionStep > 0) {
        const sourceGoods = changedItem.sourceGoods || {};
        const minBuyCount = Math.max(
          1,
          parseInt(sourceGoods.MinBuyCount, 10) || 1
        );
        quantityChange = minBuyCount;
        if (
          this.exceedsSellOutNumber(
            sourceGoods,
            changedItem.quantity,
            minBuyCount
          )
        ) {
          this.showSellOutLimitToast();
          return;
        }
      }
      const nextQuantity = this.cartItems[index].quantity + quantityChange;
      if (nextQuantity <= 0) {
        const removedQuantity = Number(changedItem.quantity) || 0;
        this.cartItems.splice(index, 1);
        this.sendTeamGoodsChange(changedItem, -removedQuantity);
      } else if (nextQuantity > 99) {
        this.$toast("单个菜品最多选择 99 份");
      } else {
        this.cartItems[index].quantity = nextQuantity;
        this.sendTeamGoodsChange(changedItem, quantityChange);
      }
    },
    /**
     * 打开购物车清空二次确认框。
     * 该方法只控制确认框显示，不直接修改购物车，避免用户误触造成数据丢失。
     *
     * @returns {void}
     */
    requestClearCart() {
      if (!this.cartItems.length) return;
      this.showClearCartConfirm = true;
    },
    /**
     * 取消清空购物车。
     * 仅关闭居中确认框，购物车条目、数量和金额保持不变。
     *
     * @returns {void}
     */
    cancelClearCart() {
      this.showClearCartConfirm = false;
    },
    /**
     * 确认清空本页面的独立购物车。
     * 不调用或修改项目原有购物车逻辑；清空后关闭确认框，但保留购物车弹窗，
     * 让用户能够立即看到“购物车还是空的”的结果。
     *
     * @returns {void}
     */
    confirmClearCart() {
      this.cartItems = [];
      this.showClearCartConfirm = false;
      this.sendTeamGoodsClear();
    },
    /**
     * 打开本页面独立的购物车弹窗。
     * 打开前关闭详情与规格弹窗，避免多个遮罩和弹层同时存在。
     *
     * @returns {void}
     */
    openCart() {
      this.showDetailPopup = false;
      this.showSpecificationPopup = false;
      this.showSetMealPopup = false;
      this.showClearCartConfirm = false;
      this.showCartPopup = true;
    },
    /**
     * 关闭购物车弹窗。
     * 购物车数据不会被清除，重新打开时仍保留用户已选菜品。
     *
     * @returns {void}
     */
    closeCart() {
      this.showClearCartConfirm = false;
      this.showCartPopup = false;
    },
    /**
     * 购物车弹窗关闭完成回调。
     * 当前购物车没有临时编辑数据，因此只保留独立回调作为后续埋点和扩展入口。
     *
     * @returns {void}
     */
    resetCartPopup() {
      this.showClearCartConfirm = false;
      // 购物车条目属于页面持久状态，关闭弹窗时不清除用户已经选择的菜品。
    },
    /**
     * 输出订单数据供后续提交页或接口使用。
     * 空购物车只提示，不触发事件；有效时同时携带条目、总数量和总金额。
     * isSubmitting 用于拦截快速连续点击；本方法只负责抛出事件，页面跳转仍按需求留空。
     *
     * @returns {void}
     */
    handleSubmit() {
      if (this.isSubmitting) return;
      if (!this.cartItems.length) {
        this.$toast("请先选择菜品");
        return;
      }
      this.isSubmitting = true;
      this.persistBlackCart();
      this.closeCart();
      this.$emit("submit-order", {
        items: this.cartItems.map((item) => ({ ...item })),
        totalCount: this.cartTotalCount,
        totalMoney: this.cartTotalMoney,
      });
      this.submitUnlockTimer = window.setTimeout(() => {
        this.isSubmitting = false;
        this.submitUnlockTimer = null;
      }, 500);

      this.$router.push({ name: "BlackCurrentConsume" });
    },
    /**
     * “查看我的订单”的页面跳转预留方法。
     * 按当前需求保持空实现，待确认目标路由后再接入 this.$router.push。
     *
     * @returns {void}
     */
    openOrders() {
      // 页面跳转预留：此处暂不执行任何路由跳转。
      this.$router.push({ name: "BlackRecordConsume" });
    },
    /**
     * 将金额统一格式化为最多两位小数。
     * 整数不显示“.00”，一位或两位有效小数会保留；非法值按 0 处理。
     *
     * @param {number|string|null|undefined} value 需要展示的金额。
     * @returns {string} 适合直接渲染的金额文本。
     */
    formatMoney(value) {
      return this.normalizePrice(value)
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.\d)0$/, "$1");
    },
    /**
     * 将 BlackMicropoint 独立购物车保存到 sessionStorage。
     * 使用独立 key 可跨路由保留数据，同时与原项目 Vuex shopCart 完全隔离。
     *
     * @returns {void}
     */
    persistBlackCart() {
      handleSessionStorage.set(
        "blackMicropointCart",
        this.cartItems.map((item) => ({ ...item }))
      );
    },
    /**
     * 进入 BlackMicropoint 时恢复独立购物车缓存。
     * 仅接受数组结构；缓存不存在或结构异常时使用空购物车。
     *
     * @returns {void}
     */
    restoreBlackCart() {
      const cachedCart = handleSessionStorage.get("blackMicropointCart");
      this.cartItems = Array.isArray(cachedCart)
        ? cachedCart.filter((item) => item && item.key)
        : [];
    },
  },
};
</script>

<style scoped lang="scss">
.black-order-page {
  min-height: 100vh;
  padding-bottom: calc(104px + env(safe-area-inset-bottom));
  color: #f8f8f6;
  background: #242424;
  color-scheme: dark;
}

.brand-hero {
  padding: 42px 24px 0;
  background: radial-gradient(circle at 78% 18%, #4e3a1c 0, #171717 40%, #111 100%);
}

.brand-copy {
  padding: 20px 0 24px;
  border-bottom: 1px solid #b98b3e;

  h1 {
    margin: 0;
    font-size: 34px;
    letter-spacing: 0;
  }

  p {
    margin: 10px 0 34px;
    color: #aaa7a2;
    font-size: 20px;
  }

  button {
    padding: 0;
    border: 0;
    color: #fff;
    background: transparent;
    font-size: 23px;
  }
}

.recommend-banner {
  position: relative;
  height: 300px;
  overflow: hidden;
  background: #111;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(0.48) sepia(0.25);
  }

  .recommend-slide {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .recommend-copy {
    position: absolute;
    left: 18px;
    bottom: 18px;
    display: flex;
    flex-direction: column;
  }

  strong {
    font-size: 30px;
  }

  span {
    color: #c7c3bd;
    font-size: 18px;
  }
}

.search-box {
  position: relative;
  margin: 36px 24px 18px;

  input {
    width: 100%;
    height: 64px;
    padding: 0 24px 0 86px;
    border: 1px solid #e0b655;
    border-radius: 6px;
    box-sizing: border-box;
    color: #fff;
    background: #686868;
    font-size: 24px;
    outline: 0;

    &::placeholder {
      color: #e8e8e8;
    }
  }

  &.is-search-active input {
    padding-right: 64px;
  }
}

.search-clear-btn {
  position: absolute;
  z-index: 3;
  right: 16px;
  top: 50%;
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 28px;
  line-height: 1;
  transform: translateY(-50%);
}

.search-icon {
  position: absolute;
  z-index: 1;
  left: 28px;
  top: 16px;
  width: 29px;
  height: 29px;
  border: 2px solid #fff;
  border-radius: 4px;
}

.menu-layout {
  display: flex;
  align-items: flex-start;
}

.category-list {
  position: sticky;
  top: 0;
  width: 158px;
  flex: 0 0 158px;
  background: #1c1c1c;

  button {
    position: relative;
    width: 100%;
    min-height: 112px;
    padding: 18px 12px;
    border: 0;
    border-right: 4px solid transparent;
    color: #e5e5e2;
    background: #1c1c1c;
    font-size: 21px;

    &.active {
      border-right-color: #f2bf49;
      color: #f5cd65;
      background: #57452f;
    }
  }

  i {
    position: absolute;
    right: 12px;
    top: 12px;
    min-width: 28px;
    height: 28px;
    border-radius: 14px;
    color: #1d1d1d;
    background: #f2c75b;
    font-size: 16px;
    font-style: normal;
    line-height: 28px;
  }
}

.goods-panel {
  min-width: 0;
  padding: 18px;
  flex: 1;
  background: #333;
}

.category-heading {
  display: flex;
  margin: 4px 0 18px;
  align-items: baseline;

  strong {
    color: #f1c453;
    font-size: 28px;
  }

  span {
    margin-left: 10px;
    color: #99906f;
    font-size: 17px;
  }
}

.goods-card {
  position: relative;
  margin-bottom: 20px;
  overflow: hidden;
  border-radius: 5px;
  background: #181818;
}

.sold-out-mask {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.72);
  font-size: 34px;
  font-weight: 600;
  letter-spacing: 6px;
}

.goods-image {
  position: relative;
  height: 210px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.recommend-tag {
  position: absolute;
  left: 0;
  top: 18px;
  padding: 9px 16px;
  border-radius: 0 5px 5px 0;
  color: #362a12;
  background: #f3cd68;
  font-size: 18px;
}

.goods-info {
  display: flex;
  min-height: 88px;
  padding: 14px 16px;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  >div:first-child {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  strong {
    overflow: hidden;
    font-size: 25px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #9c9c9a;
    font-size: 16px;
  }
}

.goods-action {
  display: flex;
  align-items: center;

  p {
    margin: 0 14px 0 0;
    font-size: 26px;
    font-weight: 600;
    white-space: nowrap;
  }

  small {
    color: #989895;
    font-size: 15px;
  }

  button {
    height: 48px;
    padding: 0 16px;
    border: 0;
    border-radius: 5px;
    color: #32260e;
    background: #f3cf70;
    font-size: 20px;
  }

  .add-button {
    width: 48px;
    padding: 0;
    border-radius: 50%;
    font-size: 27px;
  }
}

.cart-bar {
  position: fixed;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  height: calc(96px + env(safe-area-inset-bottom));
  padding: 0 24px env(safe-area-inset-bottom);
  align-items: center;
  background: #2D313F;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.28);
}

.cart-symbol {
  position: relative;
  width: 82px;
  color: #f1ce67;
  font-size: 47px;

  i {
    position: absolute;
    right: 9px;
    top: -4px;
    min-width: 27px;
    height: 27px;
    border-radius: 14px;
    color: #222;
    background: #f2c85c;
    font-size: 16px;
    font-style: normal;
    line-height: 27px;
    text-align: center;
  }
}

.cart-summary {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;

  strong {
    color: #f3ce69;
    font-size: 27px;
  }

  span {
    color: #dededb;
    font-size: 20px;
  }
}

.cart-bar>button,
.submit-button,
.spec-footer button {
  height: 68px;
  padding: 0 30px;
  border: 0;
  border-radius: 6px;
  color: #2c230e;
  background: linear-gradient(90deg, #d7aa42, #ffe78d);
  font-size: 27px;
  font-weight: 600;
}

.black-popup {
  max-height: 82vh;
  overflow: hidden;
  color: #f4f4f2;
  background: #242424;
}

.cart-popup,
.spec-popup {
  display: flex;
  max-height: 82vh;
  min-height: 430px;
  flex-direction: column;
  background: #242424;
}

.popup-header {
  display: flex;
  min-height: 84px;
  padding: 0 26px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #444;

  strong {
    font-size: 28px;
  }

  span {
    display: block;
    margin-top: 4px;
    color: #9d9d98;
    font-size: 17px;
  }

  button {
    border: 0;
    color: #f4f4f2;
    background: transparent;
    font-size: 22px;
  }
}

.cart-list,
.spec-content {
  min-height: 0;
  padding: 18px 24px;
  flex: 1;
  overflow-y: auto;
}

.cart-item {
  display: flex;
  min-height: 112px;
  padding: 14px 0;
  align-items: center;
  border-bottom: 1px solid #3d3d3d;

  img {
    width: 92px;
    height: 92px;
    border-radius: 5px;
    object-fit: cover;
  }
}

.cart-item-info {
  min-width: 0;
  margin-left: 16px;
  flex: 1;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 23px;
  }

  span {
    color: #979792;
    font-size: 16px;
  }

  p {
    margin: 8px 0 0;
    color: #f1c75e;
    font-size: 26px;
  }
}

.quantity-control {
  display: flex;
  align-items: center;

  button {
    width: 42px;
    height: 42px;
    border: 1px solid #d9b655;
    border-radius: 50%;
    color: #e5c267;
    background: transparent;
    font-size: 24px;
  }

  span {
    min-width: 46px;
    text-align: center;
  }
}

.cart-total {
  display: flex;
  height: 66px;
  padding: 0 24px;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #414141;

  strong {
    color: #f1c75e;
    font-size: 26px;
  }
}

.popup-footer {
  display: flex;
  min-height: calc(94px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  justify-content: space-between;
  border-top: 1px solid #414141;

  button {
    width: 48%;
    height: 66px;
    border-radius: 6px;
    font-size: 25px;
  }
}

.close-button {
  border: 1px solid #caa64e;
  color: #e2bd60;
  background: transparent;
}

.submit-button:disabled {
  color: #777;
  background: #444;
}

.option-group {
  margin-bottom: 28px;

  h3 {
    margin: 0 0 14px;
    color: #f0c75d;
    font-size: 23px;
  }
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  button {
    min-height: 54px;
    padding: 8px;
    border: 1px solid #555;
    border-radius: 5px;
    color: #dededb;
    background: #303030;
    font-size: 19px;

    span {
      display: block;
      color: #aaa;
      font-size: 15px;
    }

    &.active {
      border-color: #edc45c;
      color: #f4ca60;
      background: #4b4029;
    }
  }
}

.option-group textarea {
  width: 100%;
  height: 112px;
  padding: 14px;
  border: 1px solid #555;
  border-radius: 5px;
  box-sizing: border-box;
  color: #fff;
  background: #303030;
  font-size: 20px;
  outline: 0;
  resize: none;
}

.spec-footer {
  display: flex;
  min-height: calc(92px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #414141;

  div {
    display: flex;
    flex-direction: column;
  }

  span {
    color: #aaa;
    font-size: 16px;
  }

  strong {
    color: #f2c85e;
    font-size: 28px;
  }
}

.state-message {
  padding: 80px 20px;
  color: #999;
  text-align: center;
}

/* BlackMicropoint 独立视觉主题：所有尺寸均以 750px 设计稿宽度的 px 标注。 */
.black-order-page {
  height: 100vh;
  min-height: 100vh;
  padding-bottom: calc(100px + env(safe-area-inset-bottom));
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  color: #f5f3ef;
  background-color: #202631;
  background-image: url("~@/assets/black-micropoint/bg.png");
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 100% auto;
  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

.brand-hero {
  display: flex;
  height: 33.333vh;
  padding: 16px 24px 0;
  overflow: hidden;
  flex-direction: column;
  flex-shrink: 0;
  box-sizing: border-box;
  background: transparent;
}

.brand-copy {
  padding: 22px 0 20px;
  border-bottom: 0;

  h1 {
    font-size: 34px;
    font-weight: 700;
    line-height: 48px;
    letter-spacing: 1px;
  }

  p {
    margin: 4px 0 22px;
    color: #e2ddd3;
    font-size: 18px;
    line-height: 30px;
  }

  button {
    min-width: 176px;
    height: 48px;
    padding: 0 18px;
    border-radius: 24px;
    color: #2d3038;
    background: linear-gradient(135deg, #f7cf72, #eeb751);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.22);
    font-size: 21px;
    font-weight: 600;
    line-height: 48px;

    span {
      display: none;
    }
  }
}

.recommend-banner {
  height: auto;
  min-height: 0;
  margin-top: 20px;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);

  img {
    filter: none;
  }

  .recommend-copy {
    display: flex;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    min-height: 58px;
    padding: 8px 18px;
    justify-content: center;
    box-sizing: border-box;
    background: linear-gradient(90deg, rgba(28, 20, 20, 0.9), rgba(111, 32, 8, 0.86), rgba(20, 14, 13, 0.9));

    strong {
      font-size: 23px;
      line-height: 27px;
    }

    span {
      color: #eee8e1;
      font-size: 14px;
      line-height: 18px;
    }
  }

  .recommend-slide>button {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 120px;
    height: 70px;
    border: 0;
    color: #fff;
    background: transparent;
    font-size: 20px;
    opacity: 1;
  }
}

.banner-dots {
  display: flex;
  position: relative;
  z-index: 2;
  height: 26px;
  margin-top: -26px;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);

    &.active {
      background: #fff;
    }
  }
}

.search-box {
  position: sticky;
  z-index: 10;
  top: 0;
  padding: 18px 24px 14px;
  margin: 0;
  background: #202631;

  &::after {
    position: absolute;
    right: 42px;
    top: 31px;
    width: 20px;
    height: 20px;
    border: 3px solid #e6bc59;
    border-radius: 50%;
    box-sizing: border-box;
    content: "";
  }

  &::before {
    position: absolute;
    z-index: 1;
    right: 35px;
    top: 49px;
    width: 10px;
    height: 3px;
    border-radius: 2px;
    background: #e6bc59;
    content: "";
    transform: rotate(48deg);
  }

  &.is-search-active {
    &::before,
    &::after {
      display: none;
    }
  }

  input {
    height: 54px;
    padding: 0 58px 0 26px;
    border: 2px solid #697188;
    border-radius: 27px;
    background: #394155;
    font-size: 20px;

    &::placeholder {
      color: #ececec;
    }
  }

  &.is-search-active input {
    padding-right: 64px;
  }

  .search-clear-btn {
    right: 36px;
    top: calc(50% + 2px);
    color: #ececec;
    background: rgba(255, 255, 255, 0.12);
    font-size: 24px;
  }
}

.search-icon {
  left: 190px;
  top: 14px;
  width: 28px;
  height: 28px;
  border-width: 2px;
  border-radius: 3px;

  &::before,
  &::after {
    position: absolute;
    width: 8px;
    height: 8px;
    content: "";
  }

  &::before {
    left: -5px;
    top: -5px;
    border-top: 2px solid #fff;
    border-left: 2px solid #fff;
  }

  &::after {
    right: -5px;
    bottom: -5px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
  }
}

.menu-layout {
  min-height: calc(100vh - 182px - env(safe-area-inset-bottom));
  align-items: stretch;
  overflow: visible;
}

.category-list {
  position: sticky;
  top: 86px;
  width: 158px;
  max-height: calc(100vh - 182px - env(safe-area-inset-bottom));
  flex-basis: 158px;
  padding-bottom: 40px;
  border-radius: 12px 12px 0 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #374055;

  button {
    display: flex;
    min-height: 142px;
    padding: 20px 8px 16px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-right-width: 4px;
    color: #f2f2f0;
    background: #374055;
    font-size: 21px;

    &.active {
      border-right-color: transparent;
      color: #efc65f;
      background: #655a4e;

      .category-icon {
        filter: none;
      }
    }
  }

  .category-icon {
    display: block;
    width: 58px;
    height: 58px;
    margin-bottom: 7px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }

  .category-name {
    line-height: 30px;
  }
}

.goods-panel {
  padding: 18px 24px 20px;
  overflow: visible;
  background: #202631;
}

.category-heading {
  position: sticky;
  z-index: 4;
  top: 86px;
  padding: 2px 0 14px;
  margin: 2px 0 22px;
  background: #202631;

  strong {
    color: #f5f3ef;
    font-size: 25px;
  }

  span {
    color: #8f96a6;
    font-size: 17px;
  }
}

.goods-card {
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  background: #374055;
  box-shadow: 0 3px 7px rgba(0, 0, 0, 0.18);
}

.goods-image {
  height: 182px;
}

.recommend-tag {
  top: 16px;
  padding: 8px 13px;
  color: #443411;
  font-size: 18px;
  background: linear-gradient(90deg, #f0bf4e, #ffe49a);
}

.detail-button {
  position: absolute;
  right: 12px;
  bottom: 9px;
  padding: 3px 0;
  border: 0;
  color: #fff;
  background: transparent;
  font-size: 19px;
  text-shadow: 0 1px 4px #000;
}

.goods-info {
  min-height: 116px;
  padding: 10px 14px 12px;
  flex-direction: column;
  align-items: stretch;

  strong {
    color: #f5f3ef;
    font-size: 22px;
    line-height: 30px;
  }
}

.goods-action {
  margin-top: 9px;
  justify-content: space-between;

  p {
    margin-right: 12px;
    color: #f2c45c;
    font-size: 25px;

    small {
      color: #e4e4e2;
      font-size: 16px;
      font-weight: 400;
    }
  }

  button {
    height: 44px;
    min-width: 104px;
    padding: 0 18px;
    border-radius: 22px;
    color: #374055;
    font-size: 18px;
    font-weight: 600;
    background: linear-gradient(135deg, #f7d477, #efb84e);
  }
}

.card-quantity {
  button {
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
    color: #2e240d;
    border: 0;
    background: #f2cc68;
  }

  span {
    min-width: 34px;
    color: #fff;
    font-size: 26px;
  }
}

.goods-action .add-button {
  width: 44px;
  min-width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  line-height: 44px;
}

.cart-bar {
  height: calc(96px + env(safe-area-inset-bottom));
  padding: 0 24px env(safe-area-inset-bottom);
  background: #343c50;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.26);
}

.cart-symbol {
  width: 76px;
  height: 58px;
  font-size: 0;
  text-align: center;
}

.cart-bowl {
  position: absolute;
  left: 7px;
  top: 13px;
  display: block;
  width: 52px;
  height: 25px;
  border: 5px solid #efc45e;
  border-top: 0;
  border-radius: 0 0 28px 28px;
  box-sizing: border-box;
  color: transparent;

  &::before {
    position: absolute;
    left: -7px;
    top: -7px;
    width: 56px;
    height: 5px;
    border-radius: 3px;
    background: #efc45e;
    content: "";
  }

  &::after {
    position: absolute;
    left: 18px;
    top: -16px;
    width: 10px;
    height: 10px;
    border: 4px solid #efc45e;
    border-bottom: 0;
    border-radius: 9px 9px 0 0;
    content: "";
  }
}

.cart-bar>button {
  width: 208px;
  height: 68px;
  padding: 0 24px;
  border-radius: 34px;
  color: #32394b;
  background: linear-gradient(135deg, #f8d57b, #efb94f);
  font-size: 27px;
  font-weight: 600;
}

.black-popup,
.cart-popup,
.spec-popup,
.detail-popup,
.setmeal-popup,
.setmeal-change-popup {
  color: #f6f2e9;
  background-color: #242524;
  background-image: url("~@/assets/black-micropoint/bg.png");
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 100% auto;
}

.black-popup {
  border-radius: 16px 16px 0 0;
}

.clear-cart-confirm-shell {
  width: 560px;
  overflow: hidden;
  border: 1px solid rgba(232, 194, 96, 0.48);
  border-radius: 12px;
  color: #f7f3e9;
  background-color: #2E3240;
  background-image: none;
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.7);
}

.cart-popup-shell,
.cart-popup,
.detail-popup-shell,
.detail-popup,
.setmeal-popup-shell,
.setmeal-popup,
.setmeal-change-popup-shell,
.setmeal-change-popup,
.clear-cart-confirm {
  background-color: #2E3240;
  background-image: none;
}

.clear-cart-confirm {
  padding: 38px 34px 30px;
  text-align: center;

  >strong {
    display: block;
    color: #f0ca65;
    font-size: 28px;
    line-height: 40px;
  }

  >p {
    margin: 18px 0 32px;
    color: #ddd9d0;
    font-size: 21px;
    line-height: 32px;
  }

  >div {
    display: flex;
    gap: 18px;

    button {
      height: 60px;
      flex: 1;
      border-radius: 6px;
      font-size: 22px;
    }
  }

  .cancel-button {
    border: 1px solid #caa64e;
    color: #e5c168;
    background: rgba(20, 20, 20, 0.55);
  }

  .confirm-button {
    border: 0;
    color: #30250e;
    background: linear-gradient(90deg, #d7aa42, #ffe78d);
    font-weight: 600;
  }
}

.detail-popup {
  display: flex;
  max-height: 82vh;
  min-height: 450px;
  flex-direction: column;
  overflow-y: auto;

  >img {
    width: calc(100% - 48px);
    height: 390px;
    margin: 20px 24px 0;
    flex: 0 0 390px;
    border-radius: 6px;
    object-fit: cover;
  }
}

.setmeal-popup {
  display: flex;
  max-height: 82vh;
  min-height: 520px;
  flex-direction: column;
}

.setmeal-content {
  min-height: 0;
  padding: 18px 24px;
  flex: 1;
  overflow-y: auto;
}

.setmeal-group {
  margin-bottom: 26px;

  >header {
    display: flex;
    margin-bottom: 12px;
    align-items: baseline;
    justify-content: space-between;

    strong {
      color: #f0c75d;
      font-size: 23px;
    }

    span {
      color: #aaa69b;
      font-size: 16px;
    }
  }
}

.setmeal-item {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 96px;
  padding: 10px;
  margin-bottom: 10px;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  color: #f4f1e9;
  background: rgba(38, 38, 38, 0.78);
  text-align: left;

  >img {
    width: 76px;
    height: 76px;
    margin-right: 14px;
    border-radius: 5px;
    object-fit: cover;
  }

  >span {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;

    strong {
      overflow: hidden;
      font-size: 21px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      margin-top: 7px;
      overflow: hidden;
      color: #aaa69f;
      font-size: 16px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.active {
    border-color: #edc45c;
    background: rgba(83, 68, 36, 0.82);
  }
}

.setmeal-change-button {
  height: 44px;
  padding: 0 13px;
  margin-left: 12px;
  flex: 0 0 auto;
  border: 0;
  border-radius: 5px;
  color: #32260e;
  background: linear-gradient(90deg, #e5b54a, #ffe68e);
  font-size: 19px;
}

.setmeal-change-popup {
  display: flex;
  max-height: 72vh;
  min-height: 430px;
  flex-direction: column;
}

.setmeal-change-content {
  min-height: 0;
  padding: 18px 24px;
  flex: 1;
  overflow-y: auto;
}

.setmeal-change-option {
  display: flex;
  width: 100%;
  min-height: 100px;
  padding: 10px;
  margin-bottom: 12px;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  color: #f4f1e9;
  background: rgba(38, 38, 38, 0.78);
  text-align: left;

  >img {
    width: 78px;
    height: 78px;
    margin-right: 14px;
    border-radius: 5px;
    object-fit: cover;
  }

  >span {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;

    strong {
      overflow: hidden;
      font-size: 21px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      margin-top: 7px;
      overflow: hidden;
      color: #aaa69f;
      font-size: 16px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  >em {
    margin-left: 12px;
    color: #efc85f;
    font-size: 19px;
    font-style: normal;
  }

  &.active {
    border-color: #edc45c;
    background: rgba(83, 68, 36, 0.82);
  }
}

.setmeal-footer {
  flex: 0 0 auto;
  gap: 16px;

  >div:first-child {
    flex: 1 1 auto;
  }

  >button {
    height: 62px;
    padding: 0 22px;
    flex: 0 0 auto;
    font-size: 23px;
  }
}

.setmeal-quantity-area {
  min-width: 138px;
  flex: 0 0 auto;
  align-items: center;

  >span {
    margin-bottom: 5px;
  }

  .quantity-control {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    button {
      flex: 0 0 36px;
    }

    span {
      flex: 0 0 34px;
    }
  }
}

.setmeal-quantity-control {
  flex-direction: row !important;

  button {
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #d9b655;
    border-radius: 50%;
    color: #e5c267;
    background: transparent;
    font-size: 22px;
    line-height: 32px;

    &:disabled {
      border-color: #666;
      color: #666;
      background: transparent;
    }
  }

  span {
    min-width: 40px;
    color: #f6f2e9;
    font-size: 20px;
    line-height: 36px;
    text-align: center;
  }
}

.edit-setmeal-button {
  padding: 2px 0;
  margin-top: 5px;
  border: 0;
  color: #e8c160;
  background: transparent;
  font-size: 16px;
  text-align: left;
}

.detail-copy {
  padding: 18px 24px;

  p {
    margin: 0 0 14px;
    color: #c7c5bf;
    font-size: 20px;
    line-height: 32px;
  }

  strong {
    color: #f1ca63;
    font-size: 29px;
  }

  small {
    margin-left: 3px;
    color: #aaa;
    font-size: 17px;
  }
}

.detail-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.setmeal-detail-price-row {
  padding-bottom: 30px;
}

.detail-quantity-control {
  margin-left: auto;

  button:disabled {
    border-color: #666;
    color: #666;
  }

  span {
    color: #f6f2e9;
    font-size: 20px;
  }
}

.detail-setmeal-button {
  width: 208px;
  height: 68px;
  padding: 0 24px;
  margin-left: auto;
  border: 0;
  border-radius: 34px;
  color: #32394b;
  background: linear-gradient(135deg, #f8d57b, #efb94f);
  font-size: 27px;
  font-weight: 600;
}

.single-popup-footer {
  padding: 12px 24px calc(12px + env(safe-area-inset-bottom));
  margin-top: auto;
  border-top: 1px solid rgba(226, 187, 91, 0.28);

  button {
    width: 100%;
    height: 66px;
    border: 0;
    border-radius: 6px;
    color: #2d230d;
    background: linear-gradient(90deg, #d6a640, #ffe78e);
    font-size: 25px;
    font-weight: 600;
  }
}

.popup-header,
.cart-total,
.popup-footer,
.spec-footer {
  border-color: rgba(226, 187, 91, 0.28);
}

.option-grid button,
.option-group textarea {
  background: rgba(45, 45, 45, 0.76);
}

/* 弹窗底部操作按钮统一使用页面“去下单”按钮的视觉规格。 */
.popup-footer button,
.single-popup-footer button,
.spec-footer > button,
.clear-cart-confirm > div button {
  height: 68px;
  padding: 0 24px;
  border: 0;
  border-radius: 34px;
  color: #32394b;
  background: linear-gradient(135deg, #f8d57b, #efb94f);
  font-size: 27px;
  font-weight: 600;
}

.popup-footer button:disabled,
.single-popup-footer button:disabled,
.spec-footer > button:disabled,
.clear-cart-confirm > div button:disabled {
  color: #777;
  background: #444;
}

.cart-popup > .popup-footer {
  min-height: calc(114px + env(safe-area-inset-bottom));
  padding: 12px 24px calc(20px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}
</style>

<!-- 黑金 UI v2（Figma）主题，由 blackUiVersion === 2 时挂载 class 生效 -->
<style lang="scss">
@import "@/styles/black-order-theme-v2.scss";
</style>
